import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChevronDown, ChevronLeft, FileSpreadsheet, FileText, AlertCircle, AlertTriangle, SlidersHorizontal, Plus, ArrowRight, Lock, LockOpen, Building2, Send, Download, CheckCircle2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import { SUPPORTED_CURRENCIES, formatCurrency as sharedFormatCurrency } from '../utils/currency';
import { getLocationsByOrgId } from '../utils/locationApi';
import { getJournalExport, getAllJournalExport, getAccountDetail, getAccountingExportInfo, runAccountingExport } from '../utils/journalExportApi';
import { getChartOfAccounts, getJournalEntryTypes, updateJournalEntryType } from '../utils/api';
import {
  listJournalAdjustments, createJournalAdjustment,
  deleteJournalAdjustment, reverseJournalAdjustment, acknowledgeJournalAdjustment
} from '../utils/journalAdjustmentsApi';
import { closeJournalDays, reopenJournalDays } from '../utils/journalCloseoutsApi';
import { InlineSelect } from '../components/InlineTable';
import { useSourceFileViewer } from './reports/useSourceFileViewer';
// rn-* styles for the source-file picker/loading states rendered by useSourceFileViewer
import '../components/ReportsNative/ReportsNative.css';
import LoadingScreen from '../components/LoadingScreen';
import { DateRangePicker } from '../components/DateRangePicker';
import API_ENDPOINTS from '../config/apiConfig';
import { api } from '../utils/api';
import { buildJournalDetailContext, useAssistantPageContext, useAssistantDataRefresh } from '../assistant/context';

const CATEGORY_OPTIONS = [
  { value: 'revenue', label: 'Revenue', color: 'bg-green-100 text-green-700' },
  { value: 'tax', label: 'Tax', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'payments', label: 'Payments', color: 'bg-blue-100 text-blue-700' },
  // Guest/Deposit ledger movement lines that balance the month-end Oracle entry.
  { value: 'ledger', label: 'Ledger', color: 'bg-purple-100 text-purple-700' },
];

// QuickBooks-style customizable export columns. Render/export order is this
// catalog's order regardless of selection order. Amount semantics follow GL
// debit/credit convention: hotelJournal stores revenue/tax and guest-ledger
// credits as positive and payments/deposit debits as negative, so Credit =
// positive amounts, Debit = negative amounts shown positive. Net Amount is
// presented DEBIT-POSITIVE (debit − credit), the signed convention every GL
// import format (QuickBooks/Sage/NetSuite) uses — the storage sign is an
// internal encoding and must never surface to users.
const COLUMN_CATALOG = [
  { key: 'accountPrefix', label: 'Account Prefix' },
  { key: 'accountNumber', label: 'Account Number' },
  { key: 'accountSuffix', label: 'Account Suffix' },
  { key: 'accountName', label: 'Account Name' },
  { key: 'accountType', label: 'Account Type' },
  { key: 'accountGroup', label: 'Account Group' },
  { key: 'department', label: 'Department' },
  { key: 'subDepartment', label: 'Sub Department' },
  { key: 'journalTypeName', label: 'Source' },
  { key: 'category', label: 'Category' },
  { key: 'debit', label: 'Debit', numeric: true },
  { key: 'credit', label: 'Credit', numeric: true },
  { key: 'netAmount', label: 'Net Amount', numeric: true },
];

// Infinite scroll: fetch the whole range in one request, then reveal rows in
// chunks as the user scrolls (keeps the DOM light without server pagination).
const FETCH_PAGE_SIZE = 100000;
const VISIBLE_STEP = 60;

const orderColumns = (keys) => COLUMN_CATALOG.filter(c => keys.includes(c.key)).map(c => c.key);

// Amount-aware field accessor shared by the table and the CSV/Excel builders.
const columnValue = (row, key) => {
  const amount = Number(row.amount || 0);
  if (key === 'credit') return amount > 0 ? amount : null;
  if (key === 'debit') return amount < 0 ? Math.abs(amount) : null;
  if (key === 'netAmount') return amount === 0 ? 0 : -amount; // debit-positive
  return row[key] ?? null;
};

const normalizeCurrencyCode = (value, fallback = 'USD') => {
  if (!value || typeof value !== 'string') return fallback;
  const upper = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(upper) ? upper : fallback;
};

// Exact design-system semantic colors (Tailwind neutralizes blue/sky/etc → onyx,
// so the category/status hues are applied as inline styles to stay on-design).
const CAT_STYLE = {
  revenue:  { bg: 'rgba(255,122,0,0.10)', color: '#B35500', dot: '#FF7A00' },
  tax:      { bg: '#F0F9FF', color: '#0369A1', dot: '#0BA5EC' },
  payments: { bg: '#ECFDF3', color: '#15803D', dot: '#12B76A' },
  ledger:   { bg: '#E8E8F0', color: '#3A3A44', dot: '#72727F' },
  unmapped: { bg: '#FFFAEB', color: '#B54708', dot: '#F79009' },
};
const WARN = { ink: '#B54708', bg: '#FFFAEB', base: '#F79009' };
const SUCCESS = { ink: '#15803D', bg: '#ECFDF3' };

const CategoryChip = ({ category }) => {
  const key = String(category || '').toLowerCase();
  const c = CAT_STYLE[key];
  const label = key ? key.charAt(0).toUpperCase() + key.slice(1) : '—';
  if (!c) return <span className="text-slate-500">—</span>;
  return (
    <span className="inline-block px-2.5 py-[3px] rounded-full text-[11.5px] font-semibold" style={{ background: c.bg, color: c.color }}>
      {label}
    </span>
  );
};


const ToggleSwitch = ({ on, onClick, label }) => (
  <div onClick={onClick} className="flex items-center gap-2.5 cursor-pointer select-none">
    <span className="relative inline-block w-[38px] h-[22px] rounded-full flex-none transition-colors" style={{ background: on ? '#FF7A00' : '#C8C8D4' }}>
      <span className="absolute top-0.5 left-0.5 w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-transform" style={{ transform: on ? 'translateX(16px)' : 'none' }} />
    </span>
    {label && <span className="text-[13.5px] font-medium text-slate-700">{label}</span>}
  </div>
);

// Account detail modal — daily debit/credit breakdown for one account (with an
// "include adjustments" toggle), fetched from journalExport's detailAccountNumber mode.
const AccountDetailModal = ({ account, locationId, startDate, endDate, currency, formatCurrency, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdj, setShowAdj] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getAccountDetail({ locationId, startDate, endDate, accountNumber: account.accountNumber })
      .then(r => { if (alive) { setData(r.detail); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [account, locationId, startDate, endDate]);

  const summary = data?.summary || { debit: 0, credit: 0, net: 0 };
  const hasAdj = (data?.adjustments || []).length > 0;
  const rows = data
    ? [...data.daily.map(d => ({ ...d, isAdj: false })), ...(showAdj ? data.adjustments.map(d => ({ ...d, isAdj: true })) : [])]
        .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    : [];
  const catKey = String(account.category || '').toLowerCase();

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-10" style={{ background: 'rgba(15,15,17,0.5)' }} onClick={onClose}>
      <div className="w-full max-w-[620px] max-h-[calc(100vh-80px)] flex flex-col bg-white rounded-[20px] shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3.5 px-6 pt-[22px] pb-[18px] border-b border-slate-100">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 mb-1.5">
              <span className="font-mono text-[13px] text-slate-400">{account.accountNumber}</span>
              <CategoryChip category={catKey} />
            </div>
            <h3 className="text-[19px] font-bold text-slate-900">{account.accountName}</h3>
            {account.journalTypeName && account.journalTypeName !== account.accountName && (
              <div className="text-[12.5px] text-slate-400 mt-1">Source type · {account.journalTypeName}</div>
            )}
          </div>
          <button onClick={onClose} className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center text-slate-400 hover:bg-slate-100"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
        </div>

        <div className="grid grid-cols-3 gap-px bg-slate-100 border-b border-slate-100">
          {[['Debit', summary.debit], ['Credit', summary.credit], ['Net', (summary.debit || 0) - (summary.credit || 0)]].map(([label, val]) => (
            <div key={label} className="bg-white px-6 py-3.5"><div className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400 mb-1">{label}</div><div className="font-mono text-[18px] font-bold text-slate-900">{formatCurrency(val, currency)}</div></div>
          ))}
        </div>

        <div className="flex items-center gap-3 px-6 pt-3.5 pb-2.5">
          <span className="text-sm font-bold text-slate-900">Daily breakdown</span>
          <div className="flex-1" />
          {hasAdj && <ToggleSwitch on={showAdj} onClick={() => setShowAdj(v => !v)} label="Include adjustments" />}
        </div>

        <div className="overflow-y-auto flex-1 px-6 pb-2">
          {loading ? (
            <div className="py-10 text-center text-slate-400 text-sm">Loading…</div>
          ) : (
            <table className="w-full">
              <thead><tr className="sticky top-0 bg-white">
                <th className="text-left py-2 text-[11px] font-bold tracking-[0.6px] uppercase text-slate-400 border-b border-slate-100">Date</th>
                <th className="text-right py-2 text-[11px] font-bold tracking-[0.6px] uppercase text-slate-400 border-b border-slate-100">Debit</th>
                <th className="text-right py-2 text-[11px] font-bold tracking-[0.6px] uppercase text-slate-400 border-b border-slate-100">Credit</th>
              </tr></thead>
              <tbody>
                {rows.map((d, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-2.5 font-mono text-[12.5px] text-slate-600">
                      {d.date}
                      {d.isAdj && <><span className="ml-2 inline-flex items-center px-[7px] py-px rounded-full text-[10.5px] font-bold" style={{ background: 'rgba(255,122,0,0.10)', color: '#B35500' }}>Adjustment</span>{d.note && <span className="ml-2 text-[12px] text-slate-400 font-sans">{d.note}</span>}</>}
                    </td>
                    <td className="py-2.5 text-right font-mono text-[12.5px] text-slate-700">{d.debit ? formatCurrency(d.debit, currency) : ''}</td>
                    <td className="py-2.5 text-right font-mono text-[12.5px] text-slate-700">{d.credit ? formatCurrency(d.credit, currency) : ''}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="border-t-2 border-slate-200">
                <td className="py-2.5 text-[12.5px] font-bold text-slate-700">Total</td>
                <td className="py-2.5 text-right font-mono text-[13px] font-bold text-slate-900">{formatCurrency(summary.debit, currency)}</td>
                <td className="py-2.5 text-right font-mono text-[13px] font-bold text-slate-900">{formatCurrency(summary.credit, currency)}</td>
              </tr></tfoot>
            </table>
          )}
        </div>

        <div className="flex items-center justify-end px-6 py-3.5 border-t border-slate-100 bg-slate-50">
          <button onClick={onClose} className="h-10 px-5 bg-slate-900 text-white rounded-[10px] text-sm font-semibold hover:bg-slate-800">Close</button>
        </div>
      </div>
    </div>
  );
};

// Account mapping modal — tie each unmapped journal TYPE to a chart-of-account
// (and optionally its category), in-page. Same mapping as JournalTypesPage.
// "Export to accounting system": loads the format configured for the property's
// org (org-config.accountingExport), then lets the user pick a kind (income
// journal / statistics) and a path — send through the SFTP integration (marks
// the period exported; a second send is refused) or download the CSV in the
// same format (nothing is marked). Period = the month of the selected start date.
const AccountingExportModal = ({ locationId, locationName, year, month, onClose, onSent }) => {
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [kind, setKind] = useState('journal');
  const [running, setRunning] = useState(null); // 'download' | 'sftp'
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const monthLabel = new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

  const load = useCallback(async () => {
    setLoadError(null);
    try { setInfo(await getAccountingExportInfo({ locationId, year, month })); }
    catch (err) { setLoadError(err.response?.data?.error || err.message); }
  }, [locationId, year, month]);
  useEffect(() => { load(); }, [load]);

  const selected = info?.kinds?.find(k => k.kind === kind) || null;
  const alreadySent = !!selected?.exported;
  const integrationOk = !!info?.integration?.available;
  const journalClosed = info?.closeouts ? info.closeouts.closed >= info.closeouts.days : false;
  const canSend = integrationOk && !alreadySent && (kind !== 'journal' || journalClosed);

  const downloadFiles = (files) => {
    for (const f of files || []) {
      const blob = new Blob([f.content], { type: 'text/csv;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = f.fileName;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  };

  const run = async (deliver) => {
    if (deliver === 'sftp' && !window.confirm(`Send ${selected?.label || kind} for ${monthLabel} to ${info?.formatLabel}? This marks the period as exported and it cannot be sent again.`)) return;
    setRunning(deliver); setError(null); setResult(null);
    try {
      const res = await runAccountingExport({ locationId, kind, year, month, deliver });
      if (deliver === 'download') downloadFiles(res.files);
      setResult({ deliver, ...res });
      if (deliver === 'sftp') { await load(); onSent && onSent(); }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setRunning(null); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-10" style={{ background: 'rgba(15,15,17,0.5)' }} onClick={onClose}>
      <div className="w-full max-w-[600px] max-h-[calc(100vh-80px)] flex flex-col bg-white rounded-[20px] shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3.5 px-6 pt-[22px] pb-4 border-b border-slate-100">
          <span className="w-10 h-10 rounded-[11px] flex items-center justify-center flex-none" style={{ background: '#FFF4ED', color: '#E56E00' }}><Building2 size={20} /></span>
          <div className="flex-1 min-w-0">
            <h3 className="text-[18px] font-bold text-slate-900">Export to accounting system</h3>
            <div className="text-[13px] text-slate-500 mt-0.5">{locationName} · {monthLabel}{info?.formatLabel ? ` · ${info.formatLabel}` : ''}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex flex-col gap-4">
          {!info && !loadError && <div className="text-[13px] text-slate-400">Loading the format for this property…</div>}
          {loadError && <div className="text-[13px] rounded-[9px] p-3" style={{ background: '#FEF3F2', color: '#B42318' }}>{loadError}</div>}
          {info && !info.configured && (
            <div className="text-[13px] rounded-[9px] p-3" style={{ background: WARN.bg, color: WARN.ink }}>
              No accounting export format is configured for this organization. Ask Lodgeic support to set one up.
            </div>
          )}
          {info?.configured && (
            <>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400 mb-2">What to export</div>
                <div className="flex flex-col gap-2">
                  {info.kinds.map(k => (
                    <label key={k.kind} className={`flex items-start gap-3 p-3 rounded-[11px] border cursor-pointer ${kind === k.kind ? 'border-orange-400 bg-orange-50/40' : 'border-slate-200 hover:bg-slate-50'}`}>
                      <input type="radio" name="kind" className="mt-1" checked={kind === k.kind} onChange={() => { setKind(k.kind); setResult(null); setError(null); }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13.5px] font-semibold text-slate-900 flex items-center gap-2">
                          {k.label}
                          {k.exported && <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: SUCCESS.bg, color: SUCCESS.ink }}><CheckCircle2 size={11} /> Exported</span>}
                        </div>
                        <div className="text-[12px] text-slate-500 mt-0.5">
                          {k.scope === 'org' ? 'One file covering every property in the organization for the month.' : `This property's monthly journal file.`}
                          {k.exported && <span className="block mt-0.5 text-slate-400">Sent by {k.exported.exportedBy} on {fmtDate(k.exported.exportedAt)} · {k.exported.fileName}</span>}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {kind === 'journal' && info.closeouts && !journalClosed && (
                <div className="text-[12.5px] rounded-[9px] p-3" style={{ background: WARN.bg, color: WARN.ink }}>
                  {info.closeouts.closed} of {info.closeouts.days} days are closed. Every day must be closed before the journal can be sent through the integration (CSV download is still available).
                </div>
              )}
              {!integrationOk && (
                <div className="text-[12.5px] rounded-[9px] p-3 bg-slate-50 text-slate-500">The SFTP integration is not configured for this organization yet — download the file and load it manually.</div>
              )}

              {error && <div className="text-[13px] rounded-[9px] p-3" style={{ background: '#FEF3F2', color: '#B42318' }}>{error}</div>}
              {result && (
                <div className="text-[13px] rounded-[9px] p-3 flex flex-col gap-1" style={{ background: SUCCESS.bg, color: SUCCESS.ink }}>
                  <div className="font-semibold flex items-center gap-1.5"><CheckCircle2 size={14} /> {result.deliver === 'sftp' ? `Sent to ${info.formatLabel}` : 'CSV downloaded'}</div>
                  {(result.files || []).map(f => <div key={f.fileName} className="text-[12px]">{f.fileName} · {f.rows} lines{f.balanced === false ? ' · OUT OF BALANCE' : ''}</div>)}
                  {(result.warnings || []).length > 0 && (
                    <ul className="mt-1 text-[12px] list-disc pl-4" style={{ color: WARN.ink }}>
                      {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {info?.configured && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/60">
            <button onClick={() => run('download')} disabled={!!running}
              className="h-[40px] px-4 inline-flex items-center gap-2 rounded-[10px] border border-slate-200 bg-white text-[13.5px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              <Download size={15} /> {running === 'download' ? 'Preparing…' : 'Download CSV'}
            </button>
            <button onClick={() => run('sftp')} disabled={!!running || !canSend}
              title={alreadySent ? 'Already sent for this period' : !integrationOk ? 'Integration not configured' : (kind === 'journal' && !journalClosed) ? 'Close every day of the month first' : ''}
              className="h-[40px] px-[18px] inline-flex items-center gap-2 rounded-[10px] bg-orange-400 text-white text-[13.5px] font-semibold hover:bg-orange-500 disabled:opacity-40 disabled:cursor-not-allowed">
              <Send size={15} /> {running === 'sftp' ? 'Sending…' : alreadySent ? 'Already sent' : 'Send via integration'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AccountMappingModal = ({ unmappedTypes, coaOptions, orgId, currency, formatCurrency, onClose, onMapped }) => {
  const [drafts, setDrafts] = useState({});
  const [resolved, setResolved] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const catOptions = [{ value: 'revenue', label: 'Revenue' }, { value: 'tax', label: 'Tax' }, { value: 'payments', label: 'Payments' }, { value: 'ledger', label: 'Ledger' }];
  const remaining = unmappedTypes.filter(t => !resolved.has(t.journalEntryTypeId));
  const setDraft = (id, field, val) => setDrafts(d => ({ ...d, [id]: { ...(d[id] || {}), [field]: val } }));

  const mapOne = async (t) => {
    const d = drafts[t.journalEntryTypeId] || {};
    if (!d.gl) return false;
    await updateJournalEntryType(t.journalEntryTypeId, orgId, { chartOfAccountId: d.gl, ...(d.cat ? { category: d.cat } : {}) });
    setResolved(s => new Set([...s, t.journalEntryTypeId]));
    return true;
  };
  const handleMapOne = async (t) => { setSaving(true); try { await mapOne(t); } finally { setSaving(false); } };
  const handleMapAll = async () => { setSaving(true); try { for (const t of remaining) { if ((drafts[t.journalEntryTypeId] || {}).gl) await mapOne(t); } } finally { setSaving(false); } };
  const done = () => { onMapped && onMapped(); onClose(); };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-10" style={{ background: 'rgba(15,15,17,0.5)' }} onClick={onClose}>
      <div className="w-full max-w-[680px] max-h-[calc(100vh-80px)] flex flex-col bg-white rounded-[20px] shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3.5 px-6 pt-[22px] pb-4 border-b border-slate-100">
          <span className="w-10 h-10 rounded-[11px] flex items-center justify-center flex-none" style={{ background: '#FFFAEB', color: '#B54708' }}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 8 3 16 6 23 3 23 18 16 21 8 18 1 21" /><line x1="8" y1="3" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="21" /></svg></span>
          <div className="flex-1 min-w-0">
            <h3 className="text-[18px] font-bold text-slate-900">Account mapping</h3>
            <div className="text-[13px] text-slate-500 mt-0.5">Assign a chart-of-accounts entry to each unmapped journal type. Mapping clears its out-of-balance contribution.</div>
          </div>
          <button onClick={onClose} className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center text-slate-400 hover:bg-slate-100"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {remaining.length > 0 ? (
            <div className="flex flex-col gap-2.5">
              {remaining.map(t => {
                const d = drafts[t.journalEntryTypeId] || {};
                return (
                  <div key={t.journalEntryTypeId} className="border border-slate-100 rounded-[12px] p-3.5 flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900">{t.name}</div>
                        <div className="text-xs text-slate-400 mt-0.5">Source type · {t.category}</div>
                      </div>
                      <span className="font-mono text-[13.5px] font-bold" style={{ color: '#B54708' }}>{formatCurrency(t.amount, currency)}</span>
                    </div>
                    <div className="grid gap-2.5 items-end" style={{ gridTemplateColumns: '1.4fr 1fr auto' }}>
                      <label className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Map to GL account</span>
                        <InlineSelect value={d.gl ?? null} options={coaOptions} onChange={v => setDraft(t.journalEntryTypeId, 'gl', v)} placeholder="Select account" />
                      </label>
                      <label className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Category</span>
                        <InlineSelect value={d.cat ?? t.category ?? null} options={catOptions} onChange={v => setDraft(t.journalEntryTypeId, 'cat', v)} />
                      </label>
                      <button onClick={() => handleMapOne(t)} disabled={saving || !d.gl} className="h-10 px-[18px] bg-orange-400 text-white rounded-[9px] text-[13.5px] font-semibold whitespace-nowrap hover:bg-orange-500 disabled:opacity-50">Map</button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-9 flex flex-col items-center gap-3 text-center">
              <span className="w-[52px] h-[52px] rounded-[14px] flex items-center justify-center" style={{ background: '#ECFDF3', color: '#15803D' }}><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg></span>
              <div><div className="text-[15px] font-bold text-slate-900">All accounts mapped</div><div className="text-[13px] text-slate-400 mt-0.5">The journal is balanced and ready to export.</div></div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2.5 px-6 py-4 border-t border-slate-100 bg-slate-50">
          <span className="text-[13px] text-slate-500">{remaining.length} account{remaining.length === 1 ? '' : 's'} remaining</span>
          <div className="flex-1" />
          {remaining.length > 0 && (
            <button onClick={handleMapAll} disabled={saving} className="h-[42px] px-[18px] border border-slate-200 bg-white text-slate-700 rounded-[10px] text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">Map all selected</button>
          )}
          <button onClick={done} className="h-[42px] px-5 bg-slate-900 text-white rounded-[10px] text-sm font-semibold hover:bg-slate-800">Done</button>
        </div>
      </div>
    </div>
  );
};

const JournalExportPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentOrganization, isLoading: isLoadingUserSettings, userSettings, updateSettings } = useUserSettingsContext();
  const journalExportEnabled = useFeatureFlagEnabled('journal-export');
  // Per-user PostHog flag: "Export to accounting system" (Oracle Fusion SFTP / CSV).
  const accountingExportEnabled = useFeatureFlagEnabled('journal-accounting-export');

  // ---- Permission gates ----
  // Per-user journal records permission (users doc → orgAccess[org].journalPermission):
  // read = view + export only · edit = modify existing adjustments · write = full.
  // UNSET (the default) = legacy behavior — page access is governed by the
  // PostHog journal-export flag alone, with full capabilities.
  const journalPermission = (() => {
    // Prefer the org payload from initData (reflects the EMULATED user too);
    // the raw orgAccess doc is the fallback.
    const p = currentOrganization?.access?.journalPermission
      ?? userSettings?.orgAccess?.[currentOrganization?.code]?.journalPermission;
    return ['read', 'edit', 'write'].includes(p) ? p : null;
  })();
  const canWriteJournal = journalPermission === null || journalPermission === 'write';
  const canEditJournal = journalPermission === null || journalPermission !== 'read';
  // Org admin — the only role that can change (or reopen) a closed business
  // date. Prefer the org payload from initData (reflects the EMULATED user too).
  const orgRole = currentOrganization?.access?.role
    ?? userSettings?.orgAccess?.[currentOrganization?.code]?.role;
  const isOrgAdmin = userSettings?.isAdmin === 1 || orgRole === 'orgAdmin' || orgRole === 'superAdmin';
  // Org-level category gate (Cosmos org-config → journalAdjustmentCategories,
  // projected via initData): null = all categories allowed.
  const allowedAdjCategories = useMemo(() => {
    const raw = currentOrganization?.journalAdjustmentCategories;
    return Array.isArray(raw) ? raw.map((c) => String(c).toLowerCase()) : null;
  }, [currentOrganization?.journalAdjustmentCategories]);
  const categoryAllowed = useCallback(
    (cat) => allowedAdjCategories === null || allowedAdjCategories.includes(String(cat || '').toLowerCase()),
    [allowedAdjCategories]
  );
  const adjustmentsEnabled = allowedAdjCategories === null || allowedAdjCategories.length > 0;

  // Snapshot the URL filters once on first render so refresh / back-forward restore
  // the view. State below is seeded from this; the location resolves once loaded.
  const urlInit = useRef(null);
  if (urlInit.current === null) {
    urlInit.current = {
      locationId: searchParams.get('location'),
      startDate: searchParams.get('start') || '',
      endDate: searchParams.get('end') || '',
      currency: searchParams.get('currency'),
      categories: (searchParams.get('categories') || '').split(',').filter(Boolean),
      showDate: searchParams.get('showDate'),
      showUnmapped: searchParams.get('showUnmapped'),
    };
  }

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [startDate, setStartDate] = useState(urlInit.current.startDate);
  const [endDate, setEndDate] = useState(urlInit.current.endDate);
  const [selectedCategories, setSelectedCategories] = useState(urlInit.current.categories); // empty = all
  const [journalData, setJournalData] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [includeDateDetails, setIncludeDateDetails] = useState(urlInit.current.showDate === null ? true : urlInit.current.showDate !== '0');
  const [includeUnmappedData, setIncludeUnmappedData] = useState(urlInit.current.showUnmapped === null ? true : urlInit.current.showUnmapped !== '0');
  const [unmappedAccountCount, setUnmappedAccountCount] = useState(0);
  const [targetCurrency, setTargetCurrency] = useState(urlInit.current.currency || 'USD');
  const [currencyMetadata, setCurrencyMetadata] = useState(null);
  const [balance, setBalance] = useState(null); // { net, creditTotal, debitTotal, unmappedAmount, currency, balanced }
  const [adjustments, setAdjustments] = useState([]);
  const [coaOptions, setCoaOptions] = useState([]);
  // Every journalEntryType registered to the location (not just the loaded
  // range) — feeds the reclass destination picker.
  const [journalTypes, setJournalTypes] = useState([]);
  const [savingAdj, setSavingAdj] = useState(false);
  const [adjustModal, setAdjustModal] = useState(null); // { chartOfAccountId, accountLabel, category, effectiveDate, amount, note }
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [detailAccount, setDetailAccount] = useState(null); // account row for detail modal
  const [unmappedTypes, setUnmappedTypes] = useState([]);
  const [missingDates, setMissingDates] = useState([]); // past days in range with no journal rows
  // { 'YYYY-MM-DD': { status, closedBy, closedAt, ... } } — day closeout ledger for the range
  const [closeouts, setCloseouts] = useState({});
  const [closeoutBusy, setCloseoutBusy] = useState(false);
  const [expandedFlag, setExpandedFlag] = useState(null); // 'balance' | 'missing' | 'unmapped' — which flag chip is expanded
  // { [importId]: { path, date } } — archived source file behind each journal line
  const [sourceFiles, setSourceFiles] = useState({});
  const { openSourceFiles, viewer: sourceFileViewer } = useSourceFileViewer();
  const [tableView, setTableView] = useState('accounts'); // accounts | adjustments
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [accountingModal, setAccountingModal] = useState(false);
  const [accountQuery, setAccountQuery] = useState('');
  const [filterCriteria, setFilterCriteria] = useState([]); // committed search chips: [{ text, active }]
  // Infinite scroll: all rows are fetched up-front; we reveal them incrementally
  // as the user scrolls so the DOM stays light. Grand totals always reflect the
  // full set, not just what's currently rendered.
  const [visibleCount, setVisibleCount] = useState(VISIBLE_STEP);
  const sentinelRef = useRef(null);

  // Redirect if feature flag is disabled
  useEffect(() => {
    if (journalExportEnabled === false) {
      navigate('/dashboard');
    }
  }, [journalExportEnabled, navigate]);

  const isUnmappedRow = (row) => {
    const acct = (row.accountNumber || '').trim();
    return !acct || acct === 'N/A';
  };

  // Source files behind a journal line: detailed rows carry a single importId,
  // grouped rows the distinct importIds they aggregate. Manual adjustments and
  // API-synced rows (no source file) have neither, so they get no link.
  const rowSourceFiles = useCallback((row) => {
    const ids = Array.isArray(row.importIds) ? row.importIds : (row.importId != null ? [row.importId] : []);
    const seen = new Set();
    const files = [];
    for (const id of ids) {
      const f = sourceFiles[id];
      if (f?.path && !seen.has(f.path)) {
        seen.add(f.path);
        files.push({ path: f.path, date: f.date || undefined });
      }
    }
    return files;
  }, [sourceFiles]);

  // Filter out unmapped data if checkbox is unchecked
  const filteredData = useMemo(() => {
    if (includeUnmappedData) return journalData;
    return journalData.filter(row => !isUnmappedRow(row));
  }, [journalData, includeUnmappedData]);

  // Rows shown in the Accounts/All table (search-filtered; All view appends
  // adjustments). Computed at component scope so the infinite-scroll observer
  // and the grand-totals footer can share it with the table render.
  // Search covers EVERY column (dates in both ISO and M/D/YYYY, amounts with
  // and without sign). Enter commits the text as a criterion chip; a row must
  // match all active criteria plus whatever is currently being typed.
  const rowHaystack = (r) => {
    const amt = Number(r.amount || 0);
    const dateIso = r.effectiveDate ? String(r.effectiveDate).slice(0, 10) : '';
    const dateUs = dateIso ? `${Number(dateIso.slice(5, 7))}/${Number(dateIso.slice(8, 10))}/${dateIso.slice(0, 4)}` : '';
    return [
      r.accountPrefix, r.accountNumber, r.accountSuffix, r.accountName, r.accountType,
      r.accountGroup, r.department, r.subDepartment, r.journalTypeName, r.category,
      dateIso, dateUs, Math.abs(amt).toFixed(2), amt.toFixed(2),
    ].filter(Boolean).join(' ').toLowerCase();
  };
  // The backend merges adjustment rows (isManual) into the dataset in every
  // mode, so the Accounts view IS the full journal — including accounts touched
  // only by an adjustment (e.g. a reclass destination with no source rows).
  const displayRows = useMemo(() => {
    const terms = filterCriteria.filter(c => c.active).map(c => c.text.toLowerCase());
    const live = accountQuery.trim().toLowerCase();
    if (live) terms.push(live);
    const matchRow = r => terms.length === 0 || (h => terms.every(t => h.includes(t)))(rowHaystack(r));
    return filteredData.filter(matchRow);
  }, [filteredData, accountQuery, filterCriteria]);

  const commitFilterCriterion = () => {
    const text = accountQuery.trim();
    if (!text) return;
    setFilterCriteria(prev => prev.some(c => c.text.toLowerCase() === text.toLowerCase())
      ? prev
      : [...prev, { text, active: true }]);
    setAccountQuery('');
  };

  // Per-source running balance (net) + mapped account, summed over the loaded data.
  // Powers the reclass destination picker and its before/after impact preview.
  const sourceBalanceMap = useMemo(() => {
    const m = new Map();
    for (const r of journalData) {
      if (!r.journalEntryTypeId) continue;
      const ex = m.get(r.journalEntryTypeId);
      if (ex) { ex.net += Number(r.amount || 0); continue; }
      const acctLabel = `${r.accountNumber || 'N/A'}${r.accountName && r.accountName !== 'Unmapped' ? ' — ' + r.accountName : ''}`;
      m.set(r.journalEntryTypeId, {
        value: r.journalEntryTypeId,
        journalEntryTypeId: r.journalEntryTypeId,
        journalTypeName: r.journalTypeName || '',
        chartOfAccountId: r.chartOfAccountId ?? null,
        accountNumber: r.accountNumber || 'N/A',
        accountName: r.accountName || 'Unmapped',
        category: r.category,
        accountLabel: acctLabel,
        net: Number(r.amount || 0),
      });
    }
    return m;
  }, [journalData]);
  // Reclass destinations: EVERY journal type registered to the location, with
  // the loaded range's balances overlaid where present (types with no rows in
  // range carry a 0 balance). Org category gate: sources in disallowed
  // categories can't be adjustment targets (unknown-category rows stay
  // pickable — the backend re-validates).
  const sourceOptions = useMemo(() => {
    const coaLabel = (id) => coaOptions.find(o => o.value === id)?.label || '';
    const byId = new Map();
    for (const t of journalTypes) {
      if (t.isDeleted || t.id == null) continue;
      byId.set(t.id, {
        value: t.id,
        journalEntryTypeId: t.id,
        journalTypeName: t.name || '',
        chartOfAccountId: t.chartOfAccountId ?? null,
        category: t.category || null,
        accountLabel: coaLabel(t.chartOfAccountId) || 'N/A',
        accountName: '',
        net: 0,
      });
    }
    for (const s of sourceBalanceMap.values()) {
      const existing = byId.get(s.journalEntryTypeId);
      if (existing) {
        existing.net = s.net;
        existing.accountLabel = s.accountLabel || existing.accountLabel;
        existing.category = existing.category || s.category;
      } else {
        byId.set(s.journalEntryTypeId, s);
      }
    }
    return Array.from(byId.values())
      .filter(s => !s.category || categoryAllowed(s.category))
      .map(s => ({ ...s, label: s.journalTypeName || s.accountName || s.accountLabel }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [journalTypes, sourceBalanceMap, coaOptions, categoryAllowed]);

  // Reset the reveal window when the dataset or view actually changes (keyed on
  // the inputs, not the memo identity, so revealing more rows can't reset itself).
  useEffect(() => { setVisibleCount(VISIBLE_STEP); }, [journalData, adjustments, tableView, accountQuery, filterCriteria, includeUnmappedData]);

  // Reveal the next chunk as the sentinel nears the viewport. A scroll listener
  // (rather than only IntersectionObserver) avoids ref-timing races on first paint.
  const rowCount = displayRows.length;
  useEffect(() => {
    const maybeLoad = () => {
      const el = sentinelRef.current;
      if (!el) return;
      if (el.getBoundingClientRect().top < window.innerHeight + 400) {
        setVisibleCount(c => (c < rowCount ? Math.min(c + VISIBLE_STEP, rowCount) : c));
      }
    };
    window.addEventListener('scroll', maybeLoad, { passive: true });
    const obs = sentinelRef.current ? new IntersectionObserver(es => { if (es[0].isIntersecting) maybeLoad(); }, { rootMargin: '400px' }) : null;
    if (obs && sentinelRef.current) obs.observe(sentinelRef.current);
    maybeLoad();
    return () => { window.removeEventListener('scroll', maybeLoad); if (obs) obs.disconnect(); };
  }, [rowCount, visibleCount]);

  // Segmented-GL orgs (e.g. WHGS) store the account prefix/suffix segments as
  // separate numbers — only show those columns when the data carries them.
  const hasPrefix = useMemo(() => journalData.some(row => row.accountPrefix != null && row.accountPrefix !== ''), [journalData]);
  const hasSuffix = useMemo(() => journalData.some(row => row.accountSuffix != null && row.accountSuffix !== ''), [journalData]);

  // ── Column customization (QuickBooks-style) ────────────────────────────
  const [showCustomize, setShowCustomize] = useState(false);
  const [sessionColumns, setSessionColumns] = useState(null); // applied this session, unsaved
  const [draftColumns, setDraftColumns] = useState(null);     // panel checkbox state
  const [isSavingColumns, setIsSavingColumns] = useState(false);

  const savedColumnConfig = userSettings?.journalExportColumns || null;

  // Precedence: session (applied) → saved per-property → saved default →
  // built-in default (today's view, with prefix/suffix auto-shown when present).
  const activeColumns = useMemo(() => {
    if (Array.isArray(sessionColumns) && sessionColumns.length) return orderColumns(sessionColumns);
    const byLocation = savedColumnConfig?.byLocation?.[String(selectedLocation?.id)];
    if (Array.isArray(byLocation) && byLocation.length) return orderColumns(byLocation);
    if (Array.isArray(savedColumnConfig?.default) && savedColumnConfig.default.length) return orderColumns(savedColumnConfig.default);
    return [
      ...(hasPrefix ? ['accountPrefix'] : []),
      'accountNumber',
      ...(hasSuffix ? ['accountSuffix'] : []),
      'accountName', 'journalTypeName', 'category', 'debit', 'credit', 'netAmount'
    ];
  }, [sessionColumns, savedColumnConfig, selectedLocation, hasPrefix, hasSuffix]);

  // Property switches drop the unsaved session view so saved-precedence
  // resolution applies to the newly selected property.
  useEffect(() => { setSessionColumns(null); setShowCustomize(false); }, [selectedLocation?.id]);

  const openCustomize = () => {
    setDraftColumns(activeColumns);
    setShowCustomize(s => !s);
  };

  const toggleDraftColumn = (key) => {
    setDraftColumns(prev => {
      const cur = prev || [];
      return cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key];
    });
  };

  const persistColumns = async (scope) => {
    const columns = orderColumns(draftColumns || []);
    if (!columns.length) return;
    setIsSavingColumns(true);
    try {
      const next = { ...(savedColumnConfig || {}) };
      if (scope === 'location' && selectedLocation) {
        next.byLocation = { ...(next.byLocation || {}), [String(selectedLocation.id)]: columns };
      } else {
        next.default = columns;
      }
      await api.patch(API_ENDPOINTS.USER_SETTINGS, {
        ...userSettings,
        id: userSettings.email,
        journalExportColumns: next
      });
      // Sync local context/localStorage so the saved view applies without a reload.
      await updateSettings({ journalExportColumns: next });
      setSessionColumns(null);
      setShowCustomize(false);
    } catch (err) {
      setError(`Failed to save column preferences: ${err.message}`);
    } finally {
      setIsSavingColumns(false);
    }
  };

  const applyColumns = () => {
    const columns = orderColumns(draftColumns || []);
    if (!columns.length) return;
    setSessionColumns(columns);
    setShowCustomize(false);
  };

  const hasLocationConfig = Array.isArray(savedColumnConfig?.byLocation?.[String(selectedLocation?.id)]);
  const hasDefaultConfig = Array.isArray(savedColumnConfig?.default);

  const removeColumns = async (scope) => {
    setIsSavingColumns(true);
    try {
      const next = { ...(savedColumnConfig || {}) };
      if (scope === 'location' && selectedLocation) {
        const byLocation = { ...(next.byLocation || {}) };
        delete byLocation[String(selectedLocation.id)];
        next.byLocation = byLocation;
      } else {
        delete next.default;
      }
      await api.patch(API_ENDPOINTS.USER_SETTINGS, {
        ...userSettings,
        id: userSettings.email,
        journalExportColumns: next
      });
      await updateSettings({ journalExportColumns: next });
      setSessionColumns(null);
      setShowCustomize(false);
    } catch (err) {
      setError(`Failed to remove saved columns: ${err.message}`);
    } finally {
      setIsSavingColumns(false);
    }
  };

  // ── Assistant page context ──────────────────────────────────────────────
  // References only: property, loaded range, filter, display currency. The
  // balance is deliberately NOT published — the server re-reads the journal, so
  // the assistant can never be handed an imbalance by a stale render. The
  // adapter treats a single-day range as the selected business date.
  const assistantContext = useMemo(() => buildJournalDetailContext({
    orgId: currentOrganization?.id,
    locationId: selectedLocation?.id,
    locationName: selectedLocation?.name,
    startDate,
    endDate,
    categories: selectedCategories,
    displayCurrency: targetCurrency,
  }), [
    currentOrganization?.id, selectedLocation?.id, selectedLocation?.name,
    startDate, endDate, selectedCategories, targetCurrency
  ]);
  useAssistantPageContext(assistantContext);

  // fetchData is redefined every render, so the refresh subscription reads it
  // through a ref rather than depending on it — depending on it would tear down
  // and re-create the subscription continuously.
  const fetchDataRef = useRef(null);

  // When the assistant writes a correction to a day on screen, re-read the day.
  // Without this the header keeps showing the balance it fetched before the
  // write, so a user who just approved a correction is told the day is still
  // out of balance by the amount they just fixed.
  useAssistantDataRefresh(
    () => { fetchDataRef.current?.(); },
    {
      kinds: ['journal_adjustment'],
      locationId: selectedLocation?.id ?? null,
      enabled: !!selectedLocation && !!startDate && !!endDate,
    },
  );

  const locationCurrency = normalizeCurrencyCode(selectedLocation?.currency);
  const organizationCurrency = normalizeCurrencyCode(currentOrganization?.reportingCurrency);
  const currencyOptions = useMemo(() => (
    Array.from(new Set([locationCurrency, organizationCurrency, ...SUPPORTED_CURRENCIES])).filter(Boolean)
  ), [locationCurrency, organizationCurrency]);

  // Reset currency to the location default when the location changes — but honor a
  // currency that came from the URL on the very first location resolution.
  const prevLocIdRef = useRef(null);
  useEffect(() => {
    if (!selectedLocation) return;
    const firstResolve = prevLocIdRef.current === null;
    prevLocIdRef.current = selectedLocation.id;
    if (firstResolve && urlInit.current.currency) return; // keep URL currency
    setTargetCurrency(normalizeCurrencyCode(selectedLocation.currency));
    setCurrencyMetadata(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  // Mirror the active filters into the URL (replace, to avoid history spam) so a
  // refresh or back/forward restores the same view. Runs once a location resolves.
  useEffect(() => {
    if (!selectedLocation?.id) return;
    const params = { location: String(selectedLocation.id) };
    if (startDate) params.start = startDate;
    if (endDate) params.end = endDate;
    if (targetCurrency) params.currency = targetCurrency;
    if (selectedCategories.length) params.categories = selectedCategories.join(',');
    if (!includeDateDetails) params.showDate = '0';
    if (!includeUnmappedData) params.showUnmapped = '0';
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, startDate, endDate, targetCurrency, selectedCategories.join(','), includeDateDetails, includeUnmappedData]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showLocationDropdown && !e.target.closest('.location-dropdown-container')) {
        setShowLocationDropdown(false);
      }
      if (showCustomize && !e.target.closest('.customize-columns-container')) {
        setShowCustomize(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLocationDropdown, showCustomize]);

  // Load locations
  useEffect(() => {
    if (!currentOrganization || isLoadingUserSettings) return;
    getLocationsByOrgId(currentOrganization.id)
      .then(data => {
        if (data?.length) {
          setLocations(data);
          const fromUrl = urlInit.current.locationId
            ? data.find(l => String(l.id) === String(urlInit.current.locationId))
            : null;
          setSelectedLocation(fromUrl || data[0]);
        } else {
          setError('No locations found for this organization.');
        }
      })
      .catch(err => setError(`Failed to load locations: ${err.message}`));
  }, [currentOrganization, isLoadingUserSettings]);

  // Chart-of-account options for the selected location (target picker on adjustments).
  useEffect(() => {
    if (!currentOrganization?.id || !selectedLocation?.id) { setCoaOptions([]); return; }
    getChartOfAccounts(currentOrganization.id, selectedLocation.id)
      .then(accts => setCoaOptions((accts || []).map(a => ({
        value: a.id,
        label: `${a.accountNumber || a.accountPrefix || ''} — ${a.accountName || ''}`.trim(),
      }))))
      .catch(() => setCoaOptions([]));
  }, [currentOrganization?.id, selectedLocation?.id]);

  // Full journal-type registry for the location — the reclass destination
  // picker must offer every registered type, not just those with rows in the
  // currently loaded date range.
  useEffect(() => {
    if (!currentOrganization?.id || !selectedLocation?.id) { setJournalTypes([]); return; }
    getJournalEntryTypes(currentOrganization.id, selectedLocation.id)
      .then(rows => setJournalTypes(Array.isArray(rows) ? rows : []))
      .catch(() => setJournalTypes([]));
  }, [currentOrganization?.id, selectedLocation?.id]);

  const loadAdjustments = async () => {
    if (!selectedLocation?.id || !startDate || !endDate) return;
    try {
      const rows = await listJournalAdjustments({
        locationId: selectedLocation.id, orgId: currentOrganization?.id, startDate, endDate
      });
      setAdjustments(Array.isArray(rows) ? rows : []);
    } catch (err) {
      // non-fatal — the journal still renders without the adjustments panel
      console.error('Failed to load adjustments:', err);
    }
  };

  const fetchData = async () => {
    if (!selectedLocation || !startDate || !endDate) {
      setError('Please select a location and date range.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      // Infinite scroll: pull the entire range in one request; rows are revealed
      // incrementally client-side and grand totals reflect the full set.
      const result = await getJournalExport({
        locationId: selectedLocation.id,
        startDate,
        endDate,
        page: 1,
        pageSize: FETCH_PAGE_SIZE,
        groupByAccount: !includeDateDetails,
        categories: selectedCategories,
        targetCurrency,
      });
      setJournalData(result.data || []);
      setUnmappedAccountCount(result.unmappedCount || 0);
      setCurrencyMetadata(result.currency || null);
      setBalance(result.balance || null);
      setUnmappedTypes(result.unmappedTypes || []);
      setMissingDates(result.missingDates || []);
      setCloseouts(result.closeouts || {});
      setSourceFiles(result.sourceFiles || {});
      await loadAdjustments();
    } catch (err) {
      setError(`Failed to load journal data: ${err.message}`);
      setJournalData([]);
    } finally {
      setIsLoading(false);
    }
  };
  fetchDataRef.current = fetchData;

  const handleSearch = () => {
    setHasSearched(true);
    fetchData();
  };

  // ---- Manual adjustments ----
  // optimisticUpdate reconciles the local list immediately after the fetch, in case
  // the list read lags the write (read-after-write); the next fresh fetch corrects it.
  const mutateAdjustment = async (fn, id, confirmMsg, optimisticUpdate) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setSavingAdj(true);
    try {
      await fn(id);
      await fetchData();
      if (optimisticUpdate) setAdjustments(optimisticUpdate);
    } catch (err) {
      setError(`Action failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setSavingAdj(false);
    }
  };
  const handleDeleteAdjustment = (id) => mutateAdjustment(deleteJournalAdjustment, id, 'Delete this adjustment?', prev => prev.filter(a => String(a.id) !== String(id)));
  const handleReverseAdjustment = (id) => mutateAdjustment(reverseJournalAdjustment, id, 'Post a reversing (contra) entry for this adjustment?');
  const handleAcknowledgeAdjustment = (id) => mutateAdjustment(acknowledgeJournalAdjustment, id, null, prev => prev.map(a => String(a.id) === String(id) ? { ...a, needsReviewLive: false } : a));

  // ---- Day closeouts ----
  // A closed business date is locked: data feeds skip it and adjustment writes
  // are rejected server-side unless the caller is an org admin.
  const isDateClosed = useCallback(
    (iso) => closeouts[String(iso || '').slice(0, 10)]?.status === 'closed',
    [closeouts]
  );
  // Row-level lock for the action buttons. Closed days are immutable for
  // EVERYONE — org admins must reopen first (reopening is their only bypass).
  // Grouped rows carry no date — their adjustments are validated in the modal.
  const rowLockedByCloseout = useCallback(
    (row) => row?.effectiveDate != null && isDateClosed(row.effectiveDate),
    [isDateClosed]
  );
  const closedCountInRange = useMemo(
    () => Object.values(closeouts).filter(c => c?.status === 'closed').length,
    [closeouts]
  );
  const rangeDayCount = useMemo(() => {
    if (!startDate || !endDate) return 0;
    const ms = Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`);
    return ms >= 0 ? Math.round(ms / 86400000) + 1 : 0;
  }, [startDate, endDate]);
  const isSingleDay = Boolean(startDate) && startDate === endDate;
  const exportedCountInRange = useMemo(
    () => Object.values(closeouts).filter(c => c?.exportedAt).length,
    [closeouts]
  );
  const todayIso = new Date().toISOString().slice(0, 10);

  const mutateCloseout = async (fn, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setCloseoutBusy(true);
    setError(null);
    try {
      const res = await fn();
      // Out-of-balance days can't be closed — surface any the server skipped.
      if (res?.skipped?.length) {
        setError(`${res.skipped.length} day${res.skipped.length === 1 ? '' : 's'} could not be closed (out of balance): ${res.skipped.map(s => s.date).join(', ')}`);
      }
      await fetchData();
    } catch (err) {
      setError(`Action failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setCloseoutBusy(false);
    }
  };
  // Close the viewed day (single-day ranges only — closeout is deliberately
  // one day at a time; a business date only closes once its feed has arrived).
  const handleCloseDay = () => {
    if (!isSingleDay || startDate >= todayIso || isDateClosed(startDate)) return;
    return mutateCloseout(
      () => closeJournalDays({ orgId: currentOrganization.id, locationId: selectedLocation.id, dates: [startDate] }),
      'Close this day? Closed days are locked against changes from data feeds and users.'
    );
  };
  const handleReopenDay = () => mutateCloseout(
    () => reopenJournalDays({ orgId: currentOrganization.id, locationId: selectedLocation.id, dates: [startDate] }),
    'Reopen this day? It becomes editable again for users and data feeds.'
  );

  // Open the adjustment modal pre-filled from a clicked journal line. Date defaults to the
  // line's date in line-item mode, or today's date when date details aren't shown (grouped).
  const openAdjustModal = (row, mode = 'adjust') => {
    const today = new Date().toISOString().slice(0, 10);
    const category = row.category || 'revenue';
    // flipNet: the line's current net (source + adjustments) in storage sign —
    // grouped rows carry the range balance, detailed rows carry that day's amount.
    const flipNet = Number(row.amount || 0);
    setAdjustModal({
      mode, // 'adjust' (single line) | 'reclass' (move balance A → B) | 'flip' (swap debit/credit side)
      chartOfAccountId: row.chartOfAccountId ?? null,
      journalEntryTypeId: row.journalEntryTypeId ?? null,
      journalTypeName: row.journalTypeName || '',
      accountNumber: row.accountNumber || 'N/A',
      accountName: row.accountName || 'Unmapped',
      accountLabel: `${row.accountNumber || 'N/A'}${row.accountName && row.accountName !== 'Unmapped' ? ' — ' + row.accountName : ''}`,
      category,
      effectiveDate: (includeDateDetails && row.effectiveDate) ? String(row.effectiveDate).slice(0, 10) : (mode === 'flip' ? endDate : today),
      amount: '',
      // Entry side for 'adjust' mode; users think in debits/credits, never raw
      // signs. Default to the category's natural side.
      side: (category === 'payments' || category === 'ledger') ? 'debit' : 'credit',
      flipNet,
      // Reclass destination (the "to" source).
      toJournalEntryTypeId: null,
      toChartOfAccountId: null,
      toName: '',
      toCategory: null,
      toAccountLabel: '',
    });
  };

  const handleSaveModalAdjustment = async () => {
    if (!adjustModal) return;
    if (adjustModal.mode === 'flip') {
      if (!Number(adjustModal.flipNet)) { setError('This line has no balance to flip.'); return; }
    } else if (adjustModal.amount === '' || isNaN(Number(adjustModal.amount)) || Number(adjustModal.amount) === 0) { setError('Enter a non-zero amount.'); return; }
    // Adjustments anchor on a journal type; the GL account resolves from the
    // type's mapping (journalEntryTypes → ChartOfAccount).
    if (!adjustModal.journalEntryTypeId) { setError('Pick a journal type for this adjustment.'); return; }
    // Org category gate (backend enforces too — this is the friendly message).
    if (!categoryAllowed(adjustModal.category)) { setError(`This organization does not allow adjustments for the '${adjustModal.category}' category.`); return; }
    if (adjustModal.mode === 'reclass' && !categoryAllowed(adjustModal.toCategory || adjustModal.category)) { setError(`This organization does not allow adjustments for the '${adjustModal.toCategory || adjustModal.category}' category.`); return; }
    // Day-closeout gate (backend enforces too — this is the friendly message).
    if (isDateClosed(adjustModal.effectiveDate)) { setError(`Business date ${adjustModal.effectiveDate} is closed. Reopen the day (org admins only) before making changes.`); return; }
    setSavingAdj(true);
    setError(null);
    // Build a display row from data already in hand so the new adjustment shows
    // immediately — the list GET can briefly lag the write (read-after-write).
    const parseAcct = (label) => {
      const [num, ...rest] = String(label || '').split(' — ');
      return { accountNumber: num || 'N/A', accountName: rest.join(' — ') || 'Unmapped' };
    };
    const optRow = (created, fields) => ({
      id: created?.id,
      effectiveDate: adjustModal.effectiveDate,
      category: fields.category,
      amount: fields.amount,
      journalTypeName: fields.journalTypeName || '',
      accountNumber: fields.accountNumber || 'N/A',
      accountName: fields.accountName || 'Unmapped',
      createdBy: userSettings?.email,
      baselineSourceAmount: created?.baselineSourceAmount ?? null,
      needsReviewLive: false,
    });
    const optimistic = [];
    try {
      if (adjustModal.mode === 'reclass') {
        if (!adjustModal.toJournalEntryTypeId) { setError('Pick a destination journal type to reclass to.'); setSavingAdj(false); return; }
        // A reclass is two lines that net to zero. The move is signed toward the
        // source's balance so the source reduces toward 0 (or fully clears when
        // moving the whole balance); the destination receives the same amount.
        const fromNet = sourceBalanceMap.get(adjustModal.journalEntryTypeId)?.net ?? 0;
        const move = (fromNet < 0 ? -1 : 1) * Math.abs(Number(adjustModal.amount));
        const note = `Reclass: ${adjustModal.journalTypeName || adjustModal.accountLabel} → ${adjustModal.toName}`;
        // Both legs anchor on their journal type — the GL account resolves
        // live from each type's mapping, so a later remap moves them too.
        const base = { orgId: currentOrganization.id, locationId: selectedLocation.id, effectiveDate: adjustModal.effectiveDate, note, chartOfAccountId: null };
        const c1 = await createJournalAdjustment({ ...base, journalEntryTypeId: adjustModal.journalEntryTypeId, category: adjustModal.category, amount: -move });
        const c2 = await createJournalAdjustment({ ...base, journalEntryTypeId: adjustModal.toJournalEntryTypeId, category: adjustModal.toCategory || adjustModal.category, amount: move });
        optimistic.push(optRow(c1, { amount: -move, category: adjustModal.category, journalTypeName: adjustModal.journalTypeName, accountNumber: adjustModal.accountNumber, accountName: adjustModal.accountName }));
        optimistic.push(optRow(c2, { amount: move, category: adjustModal.toCategory || adjustModal.category, journalTypeName: adjustModal.toName, ...parseAcct(adjustModal.toAccountLabel) }));
      } else if (adjustModal.mode === 'flip') {
        // Flip debit <-> credit: one adjustment of -2x the current net leaves the
        // same magnitude on the opposite side. Flipping again restores the original.
        const flipNet = Number(adjustModal.flipNet);
        const fromSide = flipNet > 0 ? 'credit' : 'debit';
        const toSide = flipNet > 0 ? 'debit' : 'credit';
        const created = await createJournalAdjustment({
          orgId: currentOrganization.id,
          locationId: selectedLocation.id,
          effectiveDate: adjustModal.effectiveDate,
          journalEntryTypeId: adjustModal.journalEntryTypeId,
          chartOfAccountId: null,
          category: adjustModal.category,
          amount: -2 * flipNet,
          note: `Flip ${fromSide} → ${toSide}: ${adjustModal.journalTypeName || adjustModal.accountLabel}`,
        });
        optimistic.push(optRow(created, { amount: -2 * flipNet, category: adjustModal.category, journalTypeName: adjustModal.journalTypeName, accountNumber: adjustModal.accountNumber, accountName: adjustModal.accountName }));
      } else {
        // Users enter a positive amount + a side; the credit-positive storage
        // sign is applied here and never shown.
        const signed = (adjustModal.side === 'debit' ? -1 : 1) * Math.abs(Number(adjustModal.amount));
        const created = await createJournalAdjustment({
          orgId: currentOrganization.id,
          locationId: selectedLocation.id,
          effectiveDate: adjustModal.effectiveDate,
          journalEntryTypeId: adjustModal.journalEntryTypeId,
          chartOfAccountId: null,
          category: adjustModal.category,
          amount: signed,
        });
        optimistic.push(optRow(created, { amount: signed, category: adjustModal.category, journalTypeName: adjustModal.journalTypeName, accountNumber: adjustModal.accountNumber, accountName: adjustModal.accountName }));
      }
      setAdjustModal(null);
      setTableView('adjustments');
      await fetchData();
      // Reconcile: if the list read lagged the write, prepend the new row(s) so the
      // UI is correct immediately. The next fresh fetch replaces these by id.
      setAdjustments(prev => {
        const have = new Set(prev.map(a => String(a.id)));
        const fresh = optimistic.filter(o => o.id != null && !have.has(String(o.id)));
        return fresh.length ? [...fresh, ...prev] : prev;
      });
    } catch (err) {
      setError(`Failed to save: ${err.response?.data?.error || err.message}`);
    } finally {
      setSavingAdj(false);
    }
  };

  // Auto-run the report whenever a filter that needs a server pull/recalc changes
  // — location, date range, currency, categories, or the date-detail grouping.
  // Debounced so rapid category toggles or multi-field updates coalesce into one
  // fetch. Client-only controls (search, column customize, show-unmapped) don't refetch.
  useEffect(() => {
    if (!selectedLocation || !startDate || !endDate) return undefined;
    const t = setTimeout(() => { setHasSearched(true); fetchData(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, startDate, endDate, targetCurrency, selectedCategories.join(','), includeDateDetails]);

  const toggleCategory = (value) => {
    setSelectedCategories(prev =>
      prev.includes(value) ? prev.filter(c => c !== value) : [...prev, value]
    );
  };

  // Exports mirror the customized on-screen view exactly: same columns, same
  // catalog order, same Debit/Credit/Net Amount semantics. Effective Date is
  // governed by the Include Date Details toggle (it also controls grouping).
  const buildExportData = (data, currency) => {
    const columns = COLUMN_CATALOG.filter(c => activeColumns.includes(c.key));
    const headers = [
      ...(includeDateDetails ? ['Effective Date'] : []),
      ...columns.map(c => (c.numeric ? `${c.label} (${currency})` : c.label))
    ];
    const rows = data.map(r => [
      ...(includeDateDetails ? [new Date(r.effectiveDate).toLocaleDateString('en-US', { timeZone: 'UTC' })] : []),
      ...columns.map(c => {
        const value = columnValue(r, c.key);
        return value == null ? '' : value;
      })
    ]);
    return { headers, rows };
  };

  // Soft gate: flag (don't block) an out-of-balance export. Accountants sometimes
  // export knowingly, so we confirm rather than prevent.
  const confirmIfUnbalanced = () => {
    if (balance && !balance.balanced) {
      return window.confirm(
        `This journal is out of balance by ${formatCurrency(balance.net, balance.currency)} ` +
        `(${formatCurrency(balance.unmappedAmount, balance.currency)} from unmapped accounts). ` +
        `Export anyway?`
      );
    }
    return true;
  };

  const exportToCSV = async () => {
    if (!confirmIfUnbalanced()) return;
    try {
      setIsExporting(true);
      setError(null);
      const result = await getAllJournalExport({
        locationId: selectedLocation.id,
        startDate,
        endDate,
        groupByAccount: !includeDateDetails,
        categories: selectedCategories,
        targetCurrency,
      });
      const exportData = result.data || [];
      if (!exportData.length) { setError('No data to export.'); return; }

      const exportCurrency = result.currency?.targetCurrency || targetCurrency;
      const { headers, rows } = buildExportData(exportData, exportCurrency);
      const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `journal_export_${selectedLocation.name}_${startDate}_${endDate}.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError(`Failed to export: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const exportToExcel = async () => {
    if (!confirmIfUnbalanced()) return;
    try {
      setIsExporting(true);
      setError(null);
      const result = await getAllJournalExport({
        locationId: selectedLocation.id,
        startDate,
        endDate,
        groupByAccount: !includeDateDetails,
        categories: selectedCategories,
        targetCurrency,
      });
      const exportData = result.data || [];
      if (!exportData.length) { setError('No data to export.'); return; }

      const exportCurrency = result.currency?.targetCurrency || targetCurrency;
      const { headers, rows } = buildExportData(exportData, exportCurrency);
      const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `journal_export_${selectedLocation.name}_${startDate}_${endDate}.xlsx`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError(`Failed to export: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const formatCurrency = (amount, currency) => sharedFormatCurrency(amount || 0, currency);

  // Status flag chips (balance / missing days / unmapped) — rendered in the sticky
  // header; clicking one expands its detail panel at the top of the content area.
  const FLAG_TONES = {
    good: { bg: SUCCESS.bg, border: '#A6F4C5', ink: SUCCESS.ink },
    bad: { bg: '#FEF3F2', border: '#FECDCA', ink: '#B42318' },
    warn: { bg: WARN.bg, border: 'rgba(247,144,9,0.35)', ink: WARN.ink },
    lock: { bg: '#F8FAFC', border: '#E2E8F0', ink: '#334155' },
  };
  const toggleFlag = (k) => setExpandedFlag(f => (f === k ? null : k));
  const fmtFlagDay = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const FlagChip = ({ id, tone, icon, label }) => {
    const t = FLAG_TONES[tone];
    const open = expandedFlag === id;
    return (
      <button onClick={() => toggleFlag(id)}
        className="inline-flex items-center gap-2 h-[34px] pl-2.5 pr-3 rounded-full border text-[13px] font-bold transition-shadow"
        style={{ background: t.bg, borderColor: t.border, color: t.ink, boxShadow: open ? `0 0 0 2px ${t.border}` : 'none' }}>
        <span className="w-[22px] h-[22px] rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.6)' }}>{icon}</span>
        {label}
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s', opacity: 0.7 }}><polyline points="6 9 12 15 18 9" /></svg>
      </button>
    );
  };
  const showCloseoutChip = hasSearched && (isSingleDay || closedCountInRange > 0);
  const showFlags = hasSearched && (balance || missingDates.length > 0 || unmappedAccountCount > 0 || showCloseoutChip);

  if (isLoadingUserSettings) return <LoadingScreen message="Loading User Settings" />;

  return (
    <div className="flex flex-col w-full h-full overflow-y-auto">
      {/* STICKY PAGE HEADER — transparent + frosted so the app canvas/grid shows through
          at rest while scrolled rows stay legible behind the title */}
      <div className="sticky top-[var(--shell-sticky-top,0px)] z-30 px-7 pt-6 pb-4 flex items-end gap-5 border-b border-slate-100/60 backdrop-blur-md">
        <div className="min-w-0">
          <button
            onClick={() => navigate(`/journal-export${selectedLocation ? `?location=${selectedLocation.id}${startDate ? `&month=${String(startDate).slice(0, 7)}` : ''}` : ''}`)}
            className="flex items-center gap-0.5 text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400 hover:text-orange-500 mb-1.5 transition-colors"
            title="Back to the day-by-day overview">
            <ChevronLeft size={12} className="-ml-0.5" /> Financial Tools · Journal Overview
          </button>
          <h1 className="text-[26px] font-bold tracking-[-0.5px] text-slate-900 leading-tight">Journal Detail</h1>
          {(currentOrganization || selectedLocation) && (
            <div className="flex items-center gap-2 mt-1.5 text-slate-500 text-[13px]">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01" /></svg>
              {currentOrganization?.name}{selectedLocation ? ` · ${selectedLocation.name}` : ''}
            </div>
          )}
        </div>
        {showFlags && (
          <div className="flex items-center gap-2 flex-wrap pb-1 pl-1">
            {balance && (
              <FlagChip id="balance" tone={balance.balanced ? 'good' : 'bad'}
                icon={balance.balanced
                  ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.7 1.8 18a1 1 0 0 0 .9 1.5h18.6a1 1 0 0 0 .9-1.5L13.7 3.7a1 1 0 0 0-1.7 0Z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>}
                label={balance.balanced ? 'Balanced' : `Out of balance ${formatCurrency(-((balance.display || balance).net), (balance.display || balance).currency || balance.currency)}`} />
            )}
            {missingDates.length > 0 && (
              <FlagChip id="missing" tone="bad"
                icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="9.5" y1="14.5" x2="14.5" y2="19.5" /><line x1="14.5" y1="14.5" x2="9.5" y2="19.5" /></svg>}
                label={`${missingDates.length} missing day${missingDates.length === 1 ? '' : 's'}`} />
            )}
            {unmappedAccountCount > 0 && (
              <FlagChip id="unmapped" tone="warn"
                icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 8 3 16 6 23 3 23 18 16 21 8 18 1 21" /><line x1="8" y1="3" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="21" /></svg>}
                label={`${unmappedAccountCount} unmapped`} />
            )}
            {showCloseoutChip && (
              <FlagChip id="closeout"
                tone={isSingleDay ? (isDateClosed(startDate) ? 'lock' : 'good') : 'lock'}
                icon={isSingleDay && !isDateClosed(startDate) ? <LockOpen size={12} /> : <Lock size={12} />}
                label={isSingleDay
                  ? (isDateClosed(startDate) ? 'Day closed' : 'Day open')
                  : `${closedCountInRange} of ${rangeDayCount} days closed`} />
            )}
            {exportedCountInRange > 0 && (
              <FlagChip id="exported" tone="good"
                icon={<CheckCircle2 size={12} />}
                label={isSingleDay ? 'Exported to accounting' : `${exportedCountInRange} of ${rangeDayCount} days exported`} />
            )}
          </div>
        )}
        <div className="flex-1" />
        {hasSearched && journalData.length > 0 && (
          <div className="relative export-menu-container">
            <button
              onClick={() => setShowExportMenu(v => !v)}
              className="h-[42px] px-[18px] flex items-center gap-2.5 bg-orange-400 text-white rounded-[11px] text-sm font-semibold whitespace-nowrap hover:bg-orange-500"
              style={{ boxShadow: '0 4px 16px rgba(255,122,0,0.24)' }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              Export journal
              <ChevronDown size={15} className="opacity-85" />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 top-[50px] w-[280px] bg-white border border-slate-100 rounded-[14px] shadow-xl p-2 z-40">
                {unmappedAccountCount > 0 && (
                  <div className="flex items-start gap-2 m-0.5 mb-2 p-2.5 rounded-[9px]" style={{ background: WARN.bg, color: WARN.ink }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-none mt-0.5"><path d="M10.3 3.7 1.8 18a1 1 0 0 0 .9 1.5h18.6a1 1 0 0 0 .9-1.5L13.7 3.7a1 1 0 0 0-1.7 0Z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                    <span className="text-xs leading-snug font-medium">Journal is out of balance. Map accounts before exporting to your accounting system.</span>
                  </div>
                )}
                <div className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400 px-2.5 pt-1.5 pb-1">Download as</div>
                <button onClick={() => { setShowExportMenu(false); exportToCSV(); }} className="w-full flex items-center gap-3 p-2.5 rounded-[9px] hover:bg-slate-50 text-left">
                  <span className="w-[30px] h-[30px] rounded-lg flex items-center justify-center" style={{ background: SUCCESS.bg, color: SUCCESS.ink }}><FileText size={16} /></span>
                  <div><div className="text-[13.5px] font-semibold text-slate-900">Export CSV</div><div className="text-[11.5px] text-slate-400">Comma-separated, accounting-ready</div></div>
                </button>
                <button onClick={() => { setShowExportMenu(false); exportToExcel(); }} className="w-full flex items-center gap-3 p-2.5 rounded-[9px] hover:bg-slate-50 text-left">
                  <span className="w-[30px] h-[30px] rounded-lg flex items-center justify-center" style={{ background: '#F0F9FF', color: '#0369A1' }}><FileSpreadsheet size={16} /></span>
                  <div><div className="text-[13.5px] font-semibold text-slate-900">Export Excel</div><div className="text-[11.5px] text-slate-400">Formatted .xlsx workbook</div></div>
                </button>
                {accountingExportEnabled && (
                  <>
                    <div className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400 px-2.5 pt-2.5 pb-1 border-t border-slate-100 mt-1">Send to</div>
                    <button onClick={() => { setShowExportMenu(false); setAccountingModal(true); }} className="w-full flex items-center gap-3 p-2.5 rounded-[9px] hover:bg-slate-50 text-left">
                      <span className="w-[30px] h-[30px] rounded-lg flex items-center justify-center" style={{ background: '#FFF4ED', color: '#E56E00' }}><Building2 size={16} /></span>
                      <div><div className="text-[13.5px] font-semibold text-slate-900">Export to accounting system</div><div className="text-[11.5px] text-slate-400">Your accounting format · integration or CSV</div></div>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* CONTENT */}
      <div className="px-7 pb-10 pt-5 flex flex-col gap-4">

        {/* FLAG DETAIL PANELS — expanded from the header chips */}
        {showFlags && expandedFlag && (() => {
          const ok = !!balance?.balanced;
          const disp = balance?.display || balance || {};
          const dispCurrency = disp.currency || balance?.currency;
          const balanceTone = ok ? FLAG_TONES.good : FLAG_TONES.bad;
          return (
            <>
              {expandedFlag === 'balance' && balance && (
                <div className="rounded-2xl overflow-hidden border" style={{ background: balanceTone.bg, borderColor: balanceTone.border }}>
                  <div className="flex items-center gap-[22px] px-5 py-3.5 flex-wrap">
                    <div className="flex items-baseline gap-1.5"><span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Credits</span><span className="font-mono text-[15px] font-semibold text-slate-800">{formatCurrency(disp.creditTotal, dispCurrency)}</span></div>
                    <div className="flex items-baseline gap-1.5"><span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Debits</span><span className="font-mono text-[15px] font-semibold text-slate-800">{formatCurrency(disp.debitTotal, dispCurrency)}</span></div>
                    {!ok && Math.abs(balance.unmappedAmount || 0) > 0.005 && (
                      <div className="flex items-baseline gap-1.5"><span className="text-[11px] font-bold uppercase tracking-[0.8px]" style={{ color: balanceTone.ink }}>Unmapped contributing</span><span className="font-mono text-[15px] font-bold" style={{ color: balanceTone.ink }}>{formatCurrency(-disp.unmappedAmount, dispCurrency)}</span></div>
                    )}
                    {unmappedAccountCount > 0 && (
                      <button onClick={() => setMapModalOpen(true)} className="inline-flex items-center gap-1.5 h-[30px] px-3 rounded-lg text-[13px] font-bold border transition-colors hover:bg-orange-50" style={{ borderColor: balanceTone.ink, color: balanceTone.ink }}>
                        Resolve in mapping
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                      </button>
                    )}
                    <div className="flex-1" />
                    <span className="text-[12.5px] text-slate-400">{balance.display ? `Converted from ${balance.currency}` : `Source currency ${balance.currency}`}</span>
                  </div>
                  {Array.isArray(disp.byCategory) && disp.byCategory.length > 0 && (
                    <div className="flex items-center gap-2.5 px-5 py-[11px] border-t bg-white/50 flex-wrap" style={{ borderColor: balanceTone.border }}>
                      <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400 mr-1">Net by category</span>
                      {disp.byCategory.map(c => {
                        const st = CAT_STYLE[String(c.category || '').toLowerCase()] || CAT_STYLE.ledger;
                        return (
                          <div key={c.category} className="inline-flex items-center gap-2 h-[30px] px-3 bg-white border border-slate-100 rounded-full">
                            <span className="w-2 h-2 rounded-full" style={{ background: st.dot }} />
                            <span className="text-[12.5px] font-semibold text-slate-600 capitalize">{c.category}</span>
                            <span className="font-mono text-[12.5px] font-bold" style={{ color: st.color }}>{formatCurrency(c.net === 0 ? 0 : -c.net, dispCurrency)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {expandedFlag === 'missing' && missingDates.length > 0 && (
                <div className="flex items-center gap-3.5 px-[18px] py-3.5 rounded-2xl border flex-wrap" style={{ background: FLAG_TONES.bad.bg, borderColor: FLAG_TONES.bad.border }}>
                  <div className="flex-1 min-w-[260px]">
                    <div className="text-sm font-bold" style={{ color: FLAG_TONES.bad.ink }}>No journal data for {missingDates.slice(0, 10).map(fmtFlagDay).join(', ')}{missingDates.length > 10 ? ` +${missingDates.length - 10} more` : ''}</div>
                    <div className="text-[13px] mt-0.5" style={{ color: FLAG_TONES.bad.ink, opacity: 0.85 }}>
                      The journal feed never arrived (or failed to import) for these business dates, so totals and any export are incomplete.
                      Please re-import the data for these days, or contact support for more help.
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => navigate('/data-import')} className="h-9 px-4 flex items-center gap-1.5 text-white rounded-[9px] text-[13px] font-semibold whitespace-nowrap hover:opacity-90" style={{ background: FLAG_TONES.bad.ink }}>
                      Re-import data
                    </button>
                    {typeof window !== 'undefined' && typeof window.Intercom === 'function' && (
                      <button onClick={() => window.Intercom('show')} className="h-9 px-4 border rounded-[9px] text-[13px] font-semibold whitespace-nowrap bg-white hover:bg-red-50" style={{ borderColor: FLAG_TONES.bad.ink, color: FLAG_TONES.bad.ink }}>
                        Contact support
                      </button>
                    )}
                  </div>
                </div>
              )}

              {expandedFlag === 'unmapped' && unmappedAccountCount > 0 && (
                <div className="flex items-center gap-3.5 px-[18px] py-3.5 rounded-2xl border" style={{ background: WARN.bg, borderColor: 'rgba(247,144,9,0.35)' }}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold" style={{ color: WARN.ink }}>{unmappedAccountCount} unmapped account{unmappedAccountCount === 1 ? '' : 's'}</div>
                    <div className="text-[13px] mt-0.5" style={{ color: WARN.ink, opacity: 0.85 }}>These source accounts have no chart-of-accounts mapping, so they post to suspense and block a clean export.</div>
                  </div>
                  <button onClick={() => setMapModalOpen(true)} className="h-10 px-[18px] flex items-center gap-2 text-white rounded-[10px] text-[13.5px] font-semibold whitespace-nowrap hover:opacity-90" style={{ background: WARN.ink }}>
                    Review &amp; map
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                  </button>
                </div>
              )}

              {expandedFlag === 'exported' && exportedCountInRange > 0 && (() => {
                const t = FLAG_TONES.good;
                const rows = Object.entries(closeouts).filter(([, c]) => c?.exportedAt).sort(([a], [b]) => a.localeCompare(b));
                const last = rows[rows.length - 1][1];
                const stamp = last.exportedAt ? new Date(last.exportedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                return (
                  <div className="rounded-[12px] border px-4 py-3 text-[13px]" style={{ background: t.bg, borderColor: t.border, color: t.ink }}>
                    <div className="font-semibold">Sent to accounting system</div>
                    <div className="mt-0.5 opacity-90">
                      {rows.length} day{rows.length === 1 ? '' : 's'} in this range {rows.length === 1 ? 'was' : 'were'} exported through the integration
                      {last.exportedBy ? ` by ${last.exportedBy}` : ''}{stamp ? ` on ${stamp}` : ''}. A second send for the period is blocked; use Download CSV if the file is needed again.
                    </div>
                  </div>
                );
              })()}
              {expandedFlag === 'closeout' && showCloseoutChip && (() => {
                const c = closeouts[startDate];
                const dayClosed = isSingleDay && c?.status === 'closed';
                const t = FLAG_TONES.lock;
                const fmtStamp = (v) => {
                  const d = v ? new Date(v) : null;
                  return d && !isNaN(d) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
                };
                return (
                  <div className="flex items-center gap-3.5 px-[18px] py-3.5 rounded-2xl border flex-wrap" style={{ background: t.bg, borderColor: t.border }}>
                    <div className="flex-1 min-w-[280px]">
                      {isSingleDay ? (
                        dayClosed ? (
                          <>
                            <div className="text-sm font-bold" style={{ color: t.ink }}>
                              {fmtFlagDay(startDate)} is closed{c?.closedBy ? ` by ${c.closedBy}` : ''}{fmtStamp(c?.closedAt) ? ` on ${fmtStamp(c.closedAt)}` : ''}
                            </div>
                            <div className="text-[13px] mt-0.5" style={{ color: t.ink, opacity: 0.8 }}>
                              Data feeds and users cannot change this day.
                              {isOrgAdmin ? ' Reopen it to make changes.' : ' Contact an organization admin to reopen it.'}
                              {fmtStamp(c?.exportedAt) ? ` Exported ${fmtStamp(c.exportedAt)}.` : ''}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-sm font-bold" style={{ color: t.ink }}>{fmtFlagDay(startDate)} is open</div>
                            <div className="text-[13px] mt-0.5" style={{ color: t.ink, opacity: 0.8 }}>
                              Closing the day locks it against changes from data feeds and users. Only an organization admin can change or reopen a closed day.
                            </div>
                          </>
                        )
                      ) : (
                        <>
                          <div className="text-sm font-bold" style={{ color: t.ink }}>{closedCountInRange} of {rangeDayCount} days in this range are closed</div>
                          <div className="text-[13px] mt-0.5" style={{ color: t.ink, opacity: 0.8 }}>
                            Closed days are locked against changes from data feeds and users. Manage days one by one from the Overview.
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {canWriteJournal && isSingleDay && !dayClosed && startDate < todayIso && (() => {
                        // An out-of-balance day cannot be closed (server enforces;
                        // this disables the button when the full-day balance is known).
                        const knownUnbalanced = selectedCategories.length === 0 && balance && !balance.balanced;
                        return (
                          <button onClick={handleCloseDay} disabled={closeoutBusy || knownUnbalanced}
                            title={knownUnbalanced ? 'This day is out of balance — balance the journal before closing it' : undefined}
                            className="h-9 px-4 flex items-center gap-1.5 bg-slate-900 text-white rounded-[9px] text-[13px] font-semibold whitespace-nowrap hover:bg-slate-800 disabled:opacity-50">
                            <Lock size={13} /> {closeoutBusy ? 'Working…' : knownUnbalanced ? 'Balance to close' : 'Close day'}
                          </button>
                        );
                      })()}
                      {isSingleDay && dayClosed && isOrgAdmin && (
                        <button onClick={handleReopenDay} disabled={closeoutBusy}
                          className="h-9 px-4 flex items-center gap-1.5 border rounded-[9px] text-[13px] font-semibold whitespace-nowrap bg-white hover:bg-slate-50 disabled:opacity-50" style={{ borderColor: t.ink, color: t.ink }}>
                          <LockOpen size={13} /> {closeoutBusy ? 'Working…' : 'Reopen day'}
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/journal-export?location=${selectedLocation?.id}&month=${String(startDate).slice(0, 7)}`)}
                        className="h-9 px-4 border border-slate-200 bg-white text-slate-600 rounded-[9px] text-[13px] font-semibold whitespace-nowrap hover:bg-slate-50">
                        Manage in overview
                      </button>
                    </div>
                  </div>
                );
              })()}
            </>
          );
        })()}


        {/* FILTER TOOLBAR CARD */}
        <div className="bg-white border border-slate-100 rounded-2xl shadow-[0_1px_2px_rgba(15,15,17,0.06)] px-5 py-[18px]">
          <div className="flex items-end gap-4 flex-wrap">
            {/* Location */}
            <label className="flex flex-col gap-1.5 min-w-[200px] flex-1 relative location-dropdown-container">
              <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Location</span>
              <button
                className="w-full h-[42px] px-3.5 flex items-center justify-between border border-slate-200 rounded-[9px] bg-white text-sm font-medium text-slate-800"
                onClick={() => setShowLocationDropdown(!showLocationDropdown)}
              >
                <span className="truncate">
                  {selectedLocation ? selectedLocation.name : locations.length > 0 ? 'Select Location' : 'No Locations'}
                </span>
                <ChevronDown size={15} className="text-slate-400 flex-none" />
              </button>
              {showLocationDropdown && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-[9px] shadow-xl z-50 overflow-y-auto" style={{ maxHeight: '400px' }}>
                  {locations.map(loc => (
                    <button
                      key={loc.id}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-slate-50"
                      onClick={() => { setSelectedLocation(loc); setShowLocationDropdown(false); setJournalData([]); setHasSearched(false); }}
                    >
                      {loc.name}
                    </button>
                  ))}
                </div>
              )}
            </label>

            {/* Date Range */}
            <label className="flex flex-col gap-1.5 min-w-[230px] flex-1">
              <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Date range</span>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onDateChange={({ start, end }) => { setStartDate(start); setEndDate(end); }}
                placeholder="Select date range"
              />
            </label>

            {/* Currency */}
            <label className="flex flex-col gap-1.5 min-w-[160px]">
              <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Currency</span>
              <div className="relative">
                <select
                  className="w-full h-[42px] pl-3.5 pr-9 border border-slate-200 rounded-[9px] bg-white text-sm font-medium text-slate-800 appearance-none cursor-pointer"
                  value={targetCurrency}
                  onChange={e => { setTargetCurrency(e.target.value); setJournalData([]); setCurrencyMetadata(null); }}
                >
                  {currencyOptions.map(currency => (
                    <option key={currency} value={currency}>
                      {currency}{currency === locationCurrency ? ' (location)' : currency === organizationCurrency ? ' (org)' : ''}
                    </option>
                  ))}
                </select>
                <ChevronDown size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
              </div>
            </label>

            {/* Run report */}
            <button
              className="h-[42px] px-7 bg-slate-900 text-white rounded-[9px] text-sm font-semibold whitespace-nowrap hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSearch}
              disabled={!selectedLocation || !startDate || !endDate || isLoading}
            >
              {isLoading ? 'Loading…' : 'Run report'}
            </button>
          </div>

          {/* Categories + toggles */}
          <div className="flex items-center gap-3.5 mt-4 pt-4 border-t border-slate-100 flex-wrap">
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400 mr-0.5">Categories</span>
              {CATEGORY_OPTIONS.map(opt => {
                const on = selectedCategories.includes(opt.value);
                const c = CAT_STYLE[opt.value];
                return (
                  <button
                    key={opt.value}
                    onClick={() => toggleCategory(opt.value)}
                    className="h-8 px-3.5 rounded-full text-[13px] font-semibold border transition-colors"
                    style={on
                      ? { background: c.bg, color: c.color, borderColor: 'transparent' }
                      : { background: '#fff', color: '#52525F', borderColor: '#C8C8D4' }}
                  >
                    {opt.label}
                  </button>
                );
              })}
              {selectedCategories.length > 0 && (
                <button onClick={() => setSelectedCategories([])} className="text-[13px] font-semibold text-orange-500 hover:underline">
                  Clear
                </button>
              )}
            </div>
            <div className="flex-1" />
            <div className="flex items-center gap-[18px]">
              <ToggleSwitch on={includeDateDetails} onClick={() => setIncludeDateDetails(v => !v)} label="Show date" />
              <ToggleSwitch on={includeUnmappedData} onClick={() => setIncludeUnmappedData(v => !v)} label="Show unmapped accounts" />
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
            <AlertCircle size={20} className="mr-2 flex-shrink-0" />
            <span>{error}</span>
            <button className="ml-auto text-red-500 hover:text-red-700" onClick={() => setError(null)}>
              Dismiss
            </button>
          </div>
        )}

        {currencyMetadata?.conversionApplied && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
            Amounts converted from {locationCurrency} to {currencyMetadata.targetCurrency} using {String(currencyMetadata.granularity || 'DAILY').toLowerCase()} rates by effective date.
            {currencyMetadata.staleRates?.length > 0 ? ' Some rows used the latest prior rate.' : ''}
          </div>
        )}

        {accountingModal && selectedLocation && startDate && (
          <AccountingExportModal
            locationId={selectedLocation.id}
            locationName={selectedLocation.name}
            year={parseInt(String(startDate).slice(0, 4))}
            month={parseInt(String(startDate).slice(5, 7))}
            onClose={() => setAccountingModal(false)}
            onSent={() => fetchData()}
          />
        )}

        {/* Adjustment modal — opened by clicking a journal line; pre-fills account/category/date */}
        {adjustModal && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-10" style={{ background: 'rgba(15,15,17,0.5)' }}
            onClick={() => setAdjustModal(null)}>
            <div className="w-full max-w-[540px] bg-white rounded-[20px] shadow-2xl overflow-hidden"
              onClick={e => e.stopPropagation()}>
              <div className="flex items-start gap-3.5 px-6 pt-[22px] pb-4 border-b border-slate-100">
                <span className="w-10 h-10 rounded-[11px] flex items-center justify-center flex-none" style={{ background: '#FFF4ED', color: '#E56E00' }}><Plus size={20} /></span>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[18px] font-bold text-slate-900">{adjustModal.mode === 'reclass' ? 'Reclass balance' : adjustModal.mode === 'flip' ? 'Flip debit / credit' : 'Add adjustment'}</h3>
                  {adjustModal.accountLabel && <div className="font-mono text-[13px] text-slate-500 mt-0.5">{adjustModal.accountLabel}</div>}
                </div>
                <button onClick={() => setAdjustModal(null)} className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center text-slate-400 hover:bg-slate-100"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
              </div>
              <div className="px-6 py-[22px]">
                {/* Mode: single adjustment vs reclass (move balance A → B) vs flip (swap side) */}
                <div className="inline-flex p-1 bg-slate-100 rounded-[10px] mb-5">
                  {[['adjust', 'Adjust'], ['reclass', 'Reclass'], ['flip', 'Flip side']].map(([m, label]) => (
                    <button key={m} onClick={() => setAdjustModal(s => ({ ...s, mode: m }))}
                      disabled={m === 'flip' && !Number(adjustModal.flipNet)}
                      className={`h-8 px-4 rounded-[7px] text-[13px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${adjustModal.mode === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{label}</button>
                  ))}
                </div>

                {adjustModal.mode === 'flip' ? (() => {
                  const cur = currencyMetadata?.targetCurrency || targetCurrency;
                  const net = Number(adjustModal.flipNet);
                  const abs = Math.abs(net);
                  const fromSide = net > 0 ? 'Credit' : 'Debit';
                  const toSide = net > 0 ? 'Debit' : 'Credit';
                  return (
                    <>
                      <div className="border border-slate-200 rounded-[12px] overflow-hidden mb-4">
                        <table className="w-full text-[13px]">
                          <thead>
                            <tr className="bg-slate-50 text-[11px] font-bold uppercase tracking-[0.6px] text-slate-400">
                              <th className="text-left px-3 py-2 font-bold">Line</th>
                              <th className="text-right px-3 py-2 font-bold">Currently</th>
                              <th className="text-right px-3 py-2 font-bold">After flip</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="px-3 py-2.5 max-w-0">
                                <div className="text-[12.5px] font-medium text-slate-700 truncate">{adjustModal.journalTypeName || adjustModal.accountLabel}</div>
                                {adjustModal.accountLabel && <div className="text-[11px] text-slate-400 font-mono truncate">{adjustModal.accountLabel}</div>}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono text-[12.5px] text-slate-500 whitespace-nowrap">{formatCurrency(abs, cur)} <span className="text-[10.5px] font-bold text-slate-400">{fromSide.toUpperCase()}</span></td>
                              <td className="px-3 py-2.5 text-right font-mono text-[12.5px] font-bold text-slate-900 whitespace-nowrap">{formatCurrency(abs, cur)} <span className="text-[10.5px] font-bold text-orange-500">{toSide.toUpperCase()}</span></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <label className="flex flex-col gap-1.5 mb-4 max-w-[200px]">
                        <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Adjustment date</span>
                        <input type="date" value={adjustModal.effectiveDate}
                          onChange={e => setAdjustModal(m => ({ ...m, effectiveDate: e.target.value }))}
                          className="h-11 px-3 border border-slate-200 rounded-[9px] font-mono text-[13px] text-slate-800" />
                      </label>
                      <div className="flex items-start gap-2 px-3 py-2.5 bg-slate-50 rounded-[9px] text-slate-500 text-[12.5px] leading-relaxed">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-none mt-0.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                        <span>Posts one adjustment that moves this {fromSide.toLowerCase()} of {formatCurrency(abs, cur)} to a {toSide.toLowerCase()} of the same amount. Flipping again restores the original.</span>
                      </div>
                    </>
                  );
                })() : adjustModal.mode === 'reclass' ? (() => {
                  const cur = currencyMetadata?.targetCurrency || targetCurrency;
                  const fromSrc = sourceBalanceMap.get(adjustModal.journalEntryTypeId);
                  const fromNet = fromSrc?.net ?? 0;
                  const fromAcct = fromSrc?.accountLabel || adjustModal.accountLabel;
                  // Destination may be a registered type with no rows in the
                  // loaded range — its in-range balance is simply 0.
                  const toSrc = adjustModal.toJournalEntryTypeId
                    ? (sourceBalanceMap.get(adjustModal.toJournalEntryTypeId)
                        ?? { net: 0, accountLabel: adjustModal.toAccountLabel })
                    : null;
                  const toNet = toSrc?.net ?? 0;
                  const X = Math.abs(Number(adjustModal.amount)) || 0;
                  const move = (fromNet < 0 ? -1 : 1) * X; // signed: reduces the source toward zero
                  const fromAfter = fromNet - move;
                  const toAfter = toNet + move;
                  const fmtDelta = (d) => `${d > 0 ? '+' : d < 0 ? '−' : ''}${formatCurrency(Math.abs(d), cur)}`;
                  const deltaColor = (d) => (d < 0 ? '#52525F' : d > 0 ? '#15803D' : '#94A3B8');
                  return (
                    <>
                      {/* Directional flow: move balance FROM the clicked source TO another */}
                      <div className="flex items-end gap-2.5 mb-1.5">
                        <div className="flex-1 min-w-0">
                          <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">From</span>
                          <div className="mt-1.5 flex items-center gap-1.5 h-11 px-3 rounded-[9px] bg-slate-50 border border-slate-200 text-[13px] font-medium text-slate-700">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-none text-slate-400"><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M5 21V5a2 2 0 0 1 2-2h7l5 5v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2Z" /></svg>
                            <span className="truncate">{adjustModal.journalTypeName || adjustModal.accountLabel}</span>
                          </div>
                        </div>
                        <div className="flex-none pb-3 text-slate-400"><ArrowRight size={18} /></div>
                        <div className="flex-1 min-w-0">
                          <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">To</span>
                          <div className="mt-1.5">
                            <InlineSelect value={adjustModal.toJournalEntryTypeId}
                              options={sourceOptions.map(o => ({ value: o.value, label: o.label }))}
                              onChange={v => { const t = sourceOptions.find(o => o.value === v); setAdjustModal(m => ({ ...m, toJournalEntryTypeId: v, toChartOfAccountId: t?.chartOfAccountId ?? null, toName: t?.journalTypeName || t?.label || '', toCategory: t?.category || null, toAccountLabel: t?.accountLabel || '' })); }}
                              placeholder="Select destination" />
                          </div>
                        </div>
                      </div>
                      {/* Mapped chart-of-account for each side */}
                      <div className="flex items-start gap-2.5 mb-4 text-[11.5px] text-slate-400 font-mono">
                        <div className="flex-1 min-w-0 truncate" title={fromAcct}>{fromAcct}</div>
                        <div className="flex-none w-[18px]" />
                        <div className="flex-1 min-w-0 truncate" title={toSrc?.accountLabel || ''}>{toSrc ? toSrc.accountLabel : 'Select a destination'}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-3.5 mb-4">
                        <label className="flex flex-col gap-1.5">
                          <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Date</span>
                          <input type="date" value={adjustModal.effectiveDate}
                            onChange={e => setAdjustModal(m => ({ ...m, effectiveDate: e.target.value }))}
                            className="h-11 px-3 border border-slate-200 rounded-[9px] font-mono text-[13px] text-slate-800" />
                        </label>
                        <label className="flex flex-col gap-1.5">
                          <span className="flex items-center justify-between">
                            <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Amount to move</span>
                            <button type="button" onClick={() => setAdjustModal(m => ({ ...m, amount: String(Math.abs(fromNet)) }))}
                              className="text-[11px] font-semibold" style={{ color: '#B35500' }}>Whole balance</button>
                          </span>
                          <input type="number" step="0.01" min="0" value={adjustModal.amount} autoFocus
                            onChange={e => setAdjustModal(m => ({ ...m, amount: e.target.value }))}
                            placeholder="0" className="h-11 px-3 border border-slate-200 rounded-[9px] font-mono text-[14px] text-right text-slate-900" />
                        </label>
                      </div>
                      {/* Before · change · after for both accounts */}
                      <div className="rounded-[10px] border border-slate-200 overflow-hidden mb-3">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-slate-50 text-slate-400 text-[10px] uppercase tracking-[0.5px]">
                              <th className="text-left px-3 py-2 font-bold">Account</th>
                              <th className="text-right px-3 py-2 font-bold">Before</th>
                              <th className="text-right px-3 py-2 font-bold">Change</th>
                              <th className="text-right px-3 py-2 font-bold">After</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {[
                              { name: adjustModal.journalTypeName || adjustModal.accountLabel, acct: fromAcct, before: fromNet, after: fromAfter, known: true },
                              { name: adjustModal.toName || 'Destination', acct: toSrc?.accountLabel || '', before: toNet, after: toAfter, known: !!toSrc },
                            ].map((r, i) => (
                              <tr key={i}>
                                <td className="px-3 py-2.5 max-w-0">
                                  <div className="text-[12.5px] font-medium text-slate-700 truncate">{r.name}</div>
                                  {r.acct && <div className="text-[11px] text-slate-400 font-mono truncate">{r.acct}</div>}
                                </td>
                                <td className="px-3 py-2.5 text-right font-mono text-[12.5px] text-slate-500 whitespace-nowrap">{r.known ? formatCurrency(r.before, cur) : '—'}</td>
                                <td className="px-3 py-2.5 text-right font-mono text-[12.5px] font-semibold whitespace-nowrap" style={{ color: deltaColor(r.after - r.before) }}>{X ? fmtDelta(r.after - r.before) : '—'}</td>
                                <td className="px-3 py-2.5 text-right font-mono text-[12.5px] font-bold text-slate-900 whitespace-nowrap">{r.known ? formatCurrency(r.after, cur) : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex items-start gap-2 px-3 py-2.5 bg-slate-50 rounded-[9px] text-slate-500 text-[12.5px] leading-relaxed">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-none mt-0.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                        <span>Moves {formatCurrency(X, cur)} from the source to the destination — two adjustments that net to zero, so your totals don't change.</span>
                      </div>
                    </>
                  );
                })() : (
                  <>
                    {/* Adjustments target a JOURNAL TYPE — the GL account resolves
                        from the type's mapping (journalEntryTypes → ChartOfAccount),
                        never picked directly. */}
                    <label className="flex flex-col gap-1.5 mb-4">
                      <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Journal type</span>
                      <InlineSelect value={adjustModal.journalEntryTypeId}
                        options={sourceOptions.map(o => ({ value: o.value, label: o.label }))}
                        onChange={v => {
                          const t = sourceOptions.find(o => o.value === v);
                          const [num, ...rest] = String(t?.accountLabel || '').split(' — ');
                          setAdjustModal(m => ({
                            ...m,
                            journalEntryTypeId: v,
                            chartOfAccountId: null,
                            journalTypeName: t?.journalTypeName || t?.label || '',
                            category: t?.category || m.category,
                            accountLabel: t?.accountLabel || '',
                            accountNumber: num && num !== 'N/A' ? num : 'N/A',
                            accountName: rest.join(' — ') || 'Unmapped',
                          }));
                        }}
                        placeholder="Select journal type" />
                      <span className="text-[11.5px] text-slate-400 font-mono">
                        {adjustModal.accountLabel && !/^N\/A/.test(adjustModal.accountLabel)
                          ? `Posts to ${adjustModal.accountLabel}`
                          : 'No GL account mapped yet — map this type on the Journal Types page.'}
                      </span>
                    </label>
                    <div className="grid grid-cols-2 gap-3.5 mb-4">
                      <label className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Category</span>
                        <InlineSelect value={adjustModal.category}
                          options={[{ value: 'revenue', label: 'Revenue' }, { value: 'tax', label: 'Tax' }, { value: 'payments', label: 'Payments' }, { value: 'ledger', label: 'Ledger' }]}
                          onChange={v => setAdjustModal(m => ({ ...m, category: v }))} />
                      </label>
                      <label className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Date</span>
                        <input type="date" value={adjustModal.effectiveDate}
                          onChange={e => setAdjustModal(m => ({ ...m, effectiveDate: e.target.value }))}
                          className="h-11 px-3 border border-slate-200 rounded-[9px] font-mono text-[13px] text-slate-800" />
                      </label>
                      <label className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Side</span>
                        <div className="inline-flex p-1 bg-slate-100 rounded-[10px] h-11 items-center">
                          {[['debit', 'Debit'], ['credit', 'Credit']].map(([s, label]) => (
                            <button key={s} type="button" onClick={() => setAdjustModal(m => ({ ...m, side: s }))}
                              className={`h-8 flex-1 rounded-[7px] text-[13px] font-semibold transition-colors ${adjustModal.side === s ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{label}</button>
                          ))}
                        </div>
                      </label>
                      <label className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Amount</span>
                        <input type="number" step="0.01" min="0" value={adjustModal.amount} autoFocus
                          onChange={e => setAdjustModal(m => ({ ...m, amount: e.target.value.replace('-', '') }))}
                          placeholder="0.00" className="h-11 px-3 border border-slate-200 rounded-[9px] font-mono text-[14px] text-right text-slate-900" />
                      </label>
                    </div>
                    <div className="flex items-start gap-2 px-3 py-2.5 bg-slate-50 rounded-[9px] text-slate-500 text-[12.5px] leading-relaxed">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-none mt-0.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                      <span>Enter the amount and pick its side — no signs needed. Use Reclass to move balance to another source, or Flip side to swap an existing debit/credit.</span>
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-slate-100 bg-slate-50">
                <button onClick={() => setAdjustModal(null)} className="h-[42px] px-[18px] border border-slate-200 bg-white text-slate-700 rounded-[10px] text-sm font-semibold hover:bg-slate-50">Cancel</button>
                <button onClick={handleSaveModalAdjustment} disabled={savingAdj}
                  className="h-[42px] px-5 bg-orange-400 text-white rounded-[10px] text-sm font-semibold hover:bg-orange-500 disabled:opacity-50" style={{ boxShadow: '0 4px 16px rgba(255,122,0,0.24)' }}>
                  {savingAdj ? 'Saving…' : (adjustModal.mode === 'reclass' ? 'Post reclass' : adjustModal.mode === 'flip' ? 'Post flip' : 'Add adjustment')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ACCOUNT DETAIL MODAL */}
        {detailAccount && (
          <AccountDetailModal
            account={detailAccount}
            locationId={selectedLocation?.id}
            startDate={startDate}
            endDate={endDate}
            currency={balance?.currency || targetCurrency}
            formatCurrency={formatCurrency}
            onClose={() => setDetailAccount(null)}
          />
        )}

        {/* ACCOUNT MAPPING MODAL */}
        {mapModalOpen && (
          <AccountMappingModal
            unmappedTypes={unmappedTypes}
            coaOptions={coaOptions}
            orgId={currentOrganization?.id}
            currency={balance?.currency || targetCurrency}
            formatCurrency={formatCurrency}
            onClose={() => setMapModalOpen(false)}
            onMapped={() => fetchData()}
          />
        )}

        {/* SOURCE FILE VIEWER — modal + multi-file picker + loading toast */}
        {sourceFileViewer}

        {/* DATA CARD */}
        {hasSearched && (
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="flex items-center gap-4 px-5 py-4 border-b border-slate-100 flex-wrap">
              {/* tabs */}
              <div className="inline-flex p-[3px] bg-slate-50 border border-slate-100 rounded-[11px]">
                {[
                  { v: 'accounts', label: 'Accounts', count: filteredData.length },
                  { v: 'adjustments', label: 'Adjustments', count: adjustments.length },
                ].map(t => {
                  const active = tableView === t.v;
                  return (
                    <button key={t.v} onClick={() => setTableView(t.v)}
                      className={`h-[34px] px-4 flex items-center gap-2 rounded-lg text-[13.5px] font-semibold ${active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                      {t.label}
                      {t.count != null && (
                        <span className="font-mono text-[11px] px-[7px] py-px rounded-full" style={{ background: active ? '#FFE4CC' : '#E8E8F0', color: active ? '#B35500' : '#52525F' }}>{t.count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex-1" />
              {tableView !== 'adjustments' ? (
                <div className="flex items-center gap-2.5">
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" /></svg>
                    <input value={accountQuery} onChange={e => setAccountQuery(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitFilterCriterion(); } }}
                      placeholder="Search all columns…" title="Matches any column. Press Enter to pin the text as a criterion and keep searching."
                      className="w-[220px] h-[38px] pl-[34px] pr-3 border border-slate-200 rounded-[9px] text-[13.5px] text-slate-700 bg-white" />
                  </div>
                  <div className="relative customize-columns-container">
                    <button onClick={openCustomize} className="h-[38px] px-3.5 flex items-center gap-2 border border-slate-200 rounded-[9px] text-[13.5px] font-semibold text-slate-700 hover:bg-slate-50">
                      <SlidersHorizontal size={16} /> Customize
                    </button>
                    {showCustomize && (
                      <div className="absolute right-0 mt-1 bg-white border border-slate-200 rounded-[14px] shadow-xl z-50 w-72 p-4">
                        <div className="text-sm font-semibold text-slate-900 mb-1">Customize columns</div>
                        <div className="text-xs text-slate-400 mb-3">Applies to this table and CSV/Excel exports. Debit = negatives shown positive, Credit = positives.</div>
                        <div className="max-h-64 overflow-y-auto pr-1 mb-3">
                          {COLUMN_CATALOG.map(col => (
                            <label key={col.key} className="flex items-center gap-2 py-1 cursor-pointer text-sm text-slate-700">
                              <input type="checkbox" checked={(draftColumns || []).includes(col.key)} onChange={() => toggleDraftColumn(col.key)} className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500" />
                              {col.label}
                            </label>
                          ))}
                        </div>
                        {(draftColumns || []).length === 0 && (<div className="text-xs text-red-500 mb-2">Select at least one column.</div>)}
                        <div className="flex flex-col gap-2 border-t border-slate-100 pt-3">
                          <button className="px-3 py-1.5 bg-orange-500 text-white rounded-md text-sm font-medium hover:bg-orange-600 disabled:opacity-50" onClick={applyColumns} disabled={(draftColumns || []).length === 0}>Apply</button>
                          <button className="px-3 py-1.5 bg-white border border-slate-300 rounded-md text-sm font-medium hover:bg-slate-50 disabled:opacity-50" onClick={() => persistColumns('location')} disabled={isSavingColumns || (draftColumns || []).length === 0 || !selectedLocation}>{isSavingColumns ? 'Saving…' : `Save for ${selectedLocation?.name || 'this property'}`}</button>
                          <button className="px-3 py-1.5 bg-white border border-slate-300 rounded-md text-sm font-medium hover:bg-slate-50 disabled:opacity-50" onClick={() => persistColumns('default')} disabled={isSavingColumns || (draftColumns || []).length === 0}>{isSavingColumns ? 'Saving…' : 'Save for all my exports'}</button>
                          {(hasLocationConfig || hasDefaultConfig) && (
                            <div className="flex flex-col gap-1 border-t border-slate-100 pt-2">
                              {hasLocationConfig && (<button className="text-xs text-slate-500 hover:text-red-600 text-left disabled:opacity-50" onClick={() => removeColumns('location')} disabled={isSavingColumns}>Remove saved view for {selectedLocation?.name || 'this property'}</button>)}
                              {hasDefaultConfig && (<button className="text-xs text-slate-500 hover:text-red-600 text-left disabled:opacity-50" onClick={() => removeColumns('default')} disabled={isSavingColumns}>Remove my saved default</button>)}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : canWriteJournal && adjustmentsEnabled ? (
                <button onClick={() => openAdjustModal({})} className="h-[38px] px-4 flex items-center gap-1.5 bg-slate-900 text-white rounded-[9px] text-[13.5px] font-semibold hover:bg-slate-800">
                  <Plus size={15} /> New adjustment
                </button>
              ) : null}
            </div>

            {/* COMMITTED FILTER CRITERIA — chips with a checkbox to toggle and an X to remove.
                Rows must match every checked criterion (plus any text being typed). */}
            {filterCriteria.length > 0 && tableView !== 'adjustments' && (
              <div className="flex items-center gap-2 px-5 py-2.5 border-b border-slate-100 bg-slate-50/60 flex-wrap">
                <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Filters</span>
                {filterCriteria.map((c, i) => (
                  <span key={c.text}
                    className="inline-flex items-center gap-1.5 h-[30px] pl-2.5 pr-1.5 rounded-full border bg-white text-[13px] font-medium"
                    style={{ borderColor: c.active ? 'var(--lodge-orange-400, #FB923C)' : '#E2E8F0', color: c.active ? '#0F0F11' : '#94A3B8' }}>
                    <input type="checkbox" checked={c.active} aria-label={`Toggle filter ${c.text}`}
                      onChange={() => setFilterCriteria(prev => prev.map((p, j) => j === i ? { ...p, active: !p.active } : p))}
                      className="w-3.5 h-3.5 rounded border-slate-300 text-orange-500 focus:ring-orange-500 cursor-pointer" />
                    {c.text}
                    <button onClick={() => setFilterCriteria(prev => prev.filter((_, j) => j !== i))}
                      aria-label={`Remove filter ${c.text}`}
                      className="w-[18px] h-[18px] rounded-full flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                  </span>
                ))}
                <button onClick={() => setFilterCriteria([])} className="text-[12.5px] font-semibold text-slate-400 hover:text-slate-600 ml-1">
                  Clear all
                </button>
              </div>
            )}

            {/* BODY */}
            {isLoading ? (
              <div className="py-16 flex items-center justify-center">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-orange-400 mb-3" />
                  <p className="text-slate-500 text-sm">Loading journal data…</p>
                </div>
              </div>
            ) : tableView === 'adjustments' ? (
              adjustments.length === 0 ? (
                <div className="py-14 flex flex-col items-center gap-3.5 text-center">
                  <span className="w-[52px] h-[52px] rounded-[14px] bg-slate-50 text-slate-300 flex items-center justify-center"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg></span>
                  <div><div className="text-[15px] font-semibold text-slate-800">No adjustments yet</div><div className="text-[13px] text-slate-400 mt-1">{canWriteJournal && adjustmentsEnabled ? 'Open an account and add a top-end adjustment before export.' : !adjustmentsEnabled ? 'Adjustments are disabled for this organization.' : 'Your journal permission is view-only for adjustments.'}</div></div>
                  {canWriteJournal && adjustmentsEnabled && (
                    <button onClick={() => openAdjustModal({})} className="h-[38px] px-[18px] bg-orange-400 text-white rounded-[9px] text-[13.5px] font-semibold hover:bg-orange-500">New adjustment</button>
                  )}
                </div>
              ) : (
                <table className="w-full">
                  <thead><tr className="bg-slate-50">
                    {['Date', 'Account', 'Category', 'Amount', 'Source', 'By', 'Status', ''].map((h, i) => (
                      <th key={i} className={`px-3 py-[11px] text-[11px] font-bold tracking-[0.6px] uppercase text-slate-400 ${h === 'Amount' ? 'text-right' : 'text-left'} ${i === 0 ? 'pl-5' : ''}`}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {adjustments.map(a => {
                      const cur = balance?.currency || targetCurrency;
                      return (
                        <tr key={a.id} className="border-t border-slate-100 hover:bg-orange-50/50">
                          <td className="pl-5 pr-3 py-3.5 font-mono text-[12.5px] text-slate-600">{String(a.effectiveDate).slice(0, 10)}</td>
                          <td className="px-3 py-3.5"><span className="font-mono text-[12px] text-slate-400">{a.accountNumber}</span> <span className="text-[13.5px] font-semibold text-slate-800">{a.accountName}</span></td>
                          <td className="px-3 py-3.5"><CategoryChip category={a.category} /></td>
                          <td className="px-3 py-3.5 text-right font-mono text-[13.5px] font-semibold whitespace-nowrap" style={{ color: Number(a.amount) < 0 ? '#52525F' : '#0F0F11' }}>
                            {formatCurrency(Math.abs(Number(a.amount) || 0), cur)} <span className="text-[10.5px] font-bold text-slate-400">{Number(a.amount) < 0 ? 'DR' : 'CR'}</span>
                          </td>
                          <td className="px-3 py-3.5">
                            {a.journalTypeName
                              ? <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-50 border border-slate-200 text-[12.5px] font-medium text-slate-700"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-none text-slate-400"><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M5 21V5a2 2 0 0 1 2-2h7l5 5v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2Z" /></svg>{a.journalTypeName}</span>
                              : <span className="text-[13px] text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-3.5 text-[13px] text-slate-400">{a.createdBy}</td>
                          <td className="px-3 py-3.5">
                            {a.needsReviewLive
                              ? <span className="inline-flex items-center px-2.5 py-[3px] rounded-full text-[11.5px] font-semibold" style={{ background: WARN.bg, color: WARN.ink }}>Needs review</span>
                              : <span className="inline-flex items-center px-2.5 py-[3px] rounded-full text-[11.5px] font-semibold bg-slate-100 text-slate-600">Adjusted</span>}
                          </td>
                          <td className="px-3 pr-5 py-3.5 text-right whitespace-nowrap">
                            <div className="inline-flex gap-1.5 items-center">
                              {a.needsReviewLive && canEditJournal && <button onClick={() => handleAcknowledgeAdjustment(a.id)} className="h-[30px] px-[11px] rounded-lg text-[12.5px] font-semibold" style={{ background: WARN.bg, color: WARN.ink }}>Acknowledge</button>}
                              {rowLockedByCloseout(a) ? (
                                <span title="This business date is closed — only an organization admin can change it" className="h-[30px] px-2.5 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-400">
                                  <Lock size={12} /> Closed
                                </span>
                              ) : (
                                <>
                                  {canWriteJournal && <button onClick={() => handleReverseAdjustment(a.id)} className="h-[30px] px-[11px] border border-slate-200 bg-white text-slate-600 rounded-lg text-[12.5px] font-semibold hover:bg-slate-50">Reverse</button>}
                                  {canWriteJournal && <button onClick={() => handleDeleteAdjustment(a.id)} className="h-[30px] w-[30px] inline-flex items-center justify-center border border-slate-200 bg-white text-slate-400 rounded-lg hover:text-red-500"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg></button>}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            ) : (() => {
              const rows = displayRows;
              if (rows.length === 0) {
                return <div className="py-14 text-center text-slate-400 text-sm">{hasSearched ? 'No matching accounts for this filter.' : 'Run a report to view journal data.'}</div>;
              }
              const adjAccountSet = new Set(adjustments.map(a => a.accountNumber).filter(x => x && x !== 'N/A'));
              const cur = currencyMetadata?.targetCurrency || targetCurrency;
              const numericKeys = ['debit', 'credit', 'netAmount'];
              // Columns are driven by the Customize selection (activeColumns), plus an
              // optional Date column (Show date) and a trailing actions column.
              const cols = [
                ...(includeDateDetails ? [{ key: 'effectiveDate', label: 'Date' }] : []),
                ...COLUMN_CATALOG.filter(c => activeColumns.includes(c.key)),
              ];
              // Grand totals across the FULL set, not just the revealed window.
              const totals = {};
              numericKeys.forEach(k => { totals[k] = rows.reduce((t, r) => t + (Number(columnValue(r, k)) || 0), 0); });
              const visible = rows.slice(0, visibleCount);

              const renderCell = (key, row, isUnmapped, hasAdj) => {
                if (key === 'effectiveDate') return (
                  <span className="font-mono text-[12.5px] text-slate-500 whitespace-nowrap inline-flex items-center gap-1.5">
                    {row.effectiveDate ? new Date(row.effectiveDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—'}
                    {row.effectiveDate && isDateClosed(row.effectiveDate) && (
                      <span title="Day closed" className="text-slate-400"><Lock size={11} /></span>
                    )}
                  </span>
                );
                if (key === 'accountNumber') return <span className="font-mono text-[12.5px] text-slate-500">{row.accountNumber || 'N/A'}</span>;
                if (key === 'accountName') return (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span onClick={() => row.accountNumber && row.accountNumber !== 'N/A' && setDetailAccount(row)} className={`text-sm font-semibold text-slate-900 ${row.accountNumber && row.accountNumber !== 'N/A' ? 'cursor-pointer hover:underline' : ''}`}>{row.accountName || 'Unmapped'}</span>
                    {isUnmapped && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold" style={{ background: WARN.bg, color: WARN.ink }}><AlertTriangle size={11} />Unmapped</span>}
                    {row.isManual && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold" style={{ background: 'rgba(255,122,0,0.10)', color: '#B35500' }}>Adjustment</span>}
                    {!row.isManual && hasAdj && <span onClick={() => setTableView('adjustments')} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold cursor-pointer" style={{ background: 'rgba(255,122,0,0.10)', color: '#B35500' }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>Adjusted</span>}
                  </div>
                );
                if (key === 'journalTypeName') return row.journalTypeName
                  ? <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-50 border border-slate-200 text-[12.5px] font-medium text-slate-700 whitespace-nowrap"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-none text-slate-400"><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M5 21V5a2 2 0 0 1 2-2h7l5 5v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2Z" /></svg>{row.journalTypeName}</span>
                  : <span className="text-[13px] text-slate-300">—</span>;
                if (key === 'category') return <CategoryChip category={isUnmapped ? 'unmapped' : row.category} />;
                if (numericKeys.includes(key)) {
                  const v = columnValue(row, key);
                  if (v == null) return <span className="font-mono text-[13px] text-slate-400">—</span>;
                  return <span className={`font-mono text-[13px] ${key === 'netAmount' ? 'font-semibold' : 'text-slate-700'}`} style={key === 'netAmount' ? { color: Number(v) < 0 ? '#52525F' : '#0F0F11' } : undefined}>{formatCurrency(v, cur)}</span>;
                }
                const val = columnValue(row, key);
                return <span className="text-[13px] text-slate-600">{val == null || val === '' ? '—' : val}</span>;
              };

              return (
                <>
                  <div className="overflow-x-auto">
                  <table className="w-full" style={{ tableLayout: 'auto' }}>
                    <thead><tr className="bg-slate-50">
                      {cols.map((c, ci) => (
                        <th key={c.key} className={`py-[11px] text-[11px] font-bold tracking-[0.6px] uppercase text-slate-400 whitespace-nowrap ${numericKeys.includes(c.key) ? 'text-right' : 'text-left'} ${ci === 0 ? 'pl-5 pr-4' : 'px-4'}`}>{c.label}</th>
                      ))}
                      <th className="sticky right-0 z-20 bg-slate-50 px-5 py-[11px] w-[120px] shadow-[-6px_0_10px_-8px_rgba(15,15,17,0.12)]" />
                    </tr></thead>
                    <tbody>
                      {visible.map((row, index) => {
                        const isUnmapped = isUnmappedRow(row);
                        const hasAdj = adjAccountSet.has(row.accountNumber);
                        const srcFiles = rowSourceFiles(row);
                        return (
                          <tr key={index} className="border-t border-slate-100 hover:bg-orange-50/50 group transition-colors">
                            {cols.map((c, ci) => (
                              <td key={c.key} className={`py-3.5 ${ci === 0 ? 'pl-5 pr-4' : 'px-4'} ${numericKeys.includes(c.key) ? 'text-right' : ''}`}>
                                {renderCell(c.key, row, isUnmapped, hasAdj)}
                              </td>
                            ))}
                            <td className="sticky right-0 z-10 bg-white group-hover:bg-orange-50/50 px-5 py-3.5 text-right whitespace-nowrap shadow-[-6px_0_10px_-8px_rgba(15,15,17,0.12)]">
                              <div className="inline-flex gap-1.5 items-center">
                              {srcFiles.length > 0 && (
                                <button
                                  onClick={() => openSourceFiles(srcFiles, {
                                    title: 'Source files',
                                    subtitle: `Imported files behind ${row.accountName && row.accountName !== 'Unmapped' ? row.accountName : row.journalTypeName || 'this line'}.`,
                                    fallbackName: 'Source file',
                                  })}
                                  title={srcFiles.length === 1 ? 'View source file' : `View ${srcFiles.length} source files`}
                                  className="h-[30px] w-[30px] inline-flex items-center justify-center border border-slate-200 bg-white text-slate-400 rounded-lg hover:text-orange-500 hover:border-orange-200"
                                >
                                  <FileText size={14} />
                                </button>
                              )}
                              {rowLockedByCloseout(row) ? (
                                <span title="This business date is closed — only an organization admin can change it"
                                  className="h-8 px-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-400 opacity-70 group-hover:opacity-100 transition-opacity">
                                  <Lock size={12} /> Closed
                                </span>
                              ) : isUnmapped ? (
                                <button onClick={() => setMapModalOpen(true)} className="h-8 px-3.5 inline-flex items-center gap-1.5 text-white rounded-lg text-[13px] font-semibold hover:opacity-90" style={{ background: WARN.ink }}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 8 3 16 6 23 3 23 18 16 21 8 18 1 21" /><line x1="8" y1="3" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="21" /></svg>Map
                                </button>
                              ) : row.isManual ? (
                                canWriteJournal && (
                                  <div className="inline-flex gap-1.5">
                                    <button onClick={() => handleReverseAdjustment(row.id)} className="h-[30px] px-[11px] border border-slate-200 bg-white text-slate-600 rounded-lg text-[12.5px] font-semibold hover:bg-slate-50">Reverse</button>
                                    <button onClick={() => handleDeleteAdjustment(row.id)} className="h-[30px] w-[30px] inline-flex items-center justify-center border border-slate-200 bg-white text-slate-400 rounded-lg hover:text-red-500"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg></button>
                                  </div>
                                )
                              ) : canWriteJournal && categoryAllowed(row.category) ? (
                                <div className="inline-flex gap-1.5">
                                  {Number(row.amount || 0) !== 0 && (
                                    <button onClick={() => openAdjustModal(row, 'flip')} title="Flip this balance to the opposite side"
                                      className="h-8 px-[11px] inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-700 rounded-lg text-[13px] font-semibold opacity-70 group-hover:opacity-100 transition-opacity hover:bg-slate-50">
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></svg>
                                      Flip
                                    </button>
                                  )}
                                  <button onClick={() => openAdjustModal(row)} className="h-8 px-[13px] inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-700 rounded-lg text-[13px] font-semibold opacity-70 group-hover:opacity-100 transition-opacity hover:bg-slate-50">
                                    <Plus size={13} />Adjust
                                  </button>
                                </div>
                              ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="sticky bottom-0 z-10">
                      <tr className="border-t-2 border-slate-300 bg-white shadow-[0_-1px_0_rgba(0,0,0,0.04)]">
                        {cols.map((c, ci) => (
                          <td key={c.key} className={`py-3.5 ${ci === 0 ? 'pl-5 pr-4' : 'px-4'} ${numericKeys.includes(c.key) ? 'text-right' : ''}`}>
                            {ci === 0
                              ? <span className="text-[11px] font-bold uppercase tracking-[0.6px] text-slate-500">Grand total · {rows.length} {rows.length === 1 ? 'row' : 'rows'}</span>
                              : numericKeys.includes(c.key)
                                ? <span className="font-mono text-[13px] font-bold" style={{ color: c.key === 'netAmount' && totals[c.key] < 0 ? '#52525F' : '#0F0F11' }}>{formatCurrency(totals[c.key], cur)}</span>
                                : null}
                          </td>
                        ))}
                        <td className="sticky right-0 z-10 px-5 bg-white shadow-[-6px_0_10px_-8px_rgba(15,15,17,0.12)]" />
                      </tr>
                    </tfoot>
                  </table>
                  </div>
                  {visible.length < rows.length && (
                    <div ref={sentinelRef} className="py-5 flex items-center justify-center gap-2 text-[12.5px] text-slate-400">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                      Loading more · {visible.length} of {rows.length}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default JournalExportPage;
